import type { UploadIdentificationCommandResultStatus } from "Api/Api";
import type { FeesPeriodVariant } from "Components/Dashboard/TransactionFees/AdminTransactionFees";
import type {
  DeclinedReason,
  AdditionalInfoStatus,
  AddressType,
  AverageTransactionValue,
  Bank,
  BankEnvironment,
  BankVerificationSource,
  BankVerificationStatus,
  BusinessActivity,
  EmployeeStatus,
  ExpectedMonthlyRevenue,
  IdentityUserStatus,
  IdentityVerificationStatus,
  LeadStatus,
  LicenseCode,
  LicensePaymentStatus,
  LoyaltyEventCode,
  MerchantStatus,
  MerchantTerminalContractType,
  NotificationVariant,
  PartyType,
  ProductCode,
  RelationType,
  RiskProfileStatus,
  SettlementStatus,
  SourceOfIncome,
  TerminalCountChangeSource,
  TerminalCountChangeStatus,
  TransactionStatus,
  UploadIdentificationCommandResultStatus2,
  UserStatus,
  VatStatus,
  Version,
} from "Infrastructure/Api/Api";
import type { AccessRoleCode } from "Models/AccessRoleCodes";
import type { BusinessRelationType } from "Models/BusinessRelationTypes";
import type { ProductPricingCategory } from "Types/MerchantPricingCategories";
import type { TimePeriod } from "Types/TimePeriod";
import type { WeekDay } from "Types/WeekDay";
import type { AffiliateUserRole } from "Types/types";
import i18next, { type ResourceLanguage } from "i18next";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { createProxy, getPath, type ObjPathProxy } from "ts-object-path";

/**
 * Utility type for supporting i18next pluralization.
 * Wraps an object type to allow plural suffix keys (_one, _other, _few, _many, _zero, _two)
 * while making base keys optional in the implementation.
 *
 * Example:
 * ```typescript
 * TimePassed: WithPlurals<{
 *   Now: string;
 *   Minute: string;
 * }>
 * ```
 *
 * This allows the implementation dictionaries to have only plural forms:
 * ```typescript
 * Minute_one: "{{count}} minute ago",
 * Minute_other: "{{count}} minutes ago"
 * ```
 *
 * While still providing autocomplete for the base keys (Minute, Hour, etc.)
 * when calling `t(resources.Minute, { count })`.
 *
 * The type uses a conditional to ensure that when accessed through ObjPathProxy,
 * base keys are treated as string (not undefined), while allowing implementations
 * to omit them in favor of plural forms.
 */
type PluralSuffix =
  | ""
  | "_zero"
  | "_one"
  | "_two"
  | "_few"
  | "_many"
  | "_other";

export type WithPlurals<T> = {
  [K in keyof T as K extends string ? `${K}${PluralSuffix}` : never]?: string;
} & {
  [K in keyof T]: string;
};

/** Role families that have a social surface and therefore their own set of Chat documents. */
export type SocialTermsDocumentRole = "Customer" | "Merchant" | "Hunter";

type SocialTermsDocument = {
  Title: string;
  Url: Record<SocialTermsDocumentRole, string>;
};

export interface ResourceDictionary extends ResourceLanguage {
  Stories: {
    Bar: {
      YourStory: string;
    };
    ImageUpload: {
      Prompt: string;
      Error: string;
      InvalidType: string;
      TooLarge: string;
    };
    VideoUpload: {
      Prompt: string;
      Error: string;
      TooLong: string;
      InvalidType: string;
      TooLarge: string;
    };
    Management: {
      Title: string;
      CreateButton: string;
      Empty: string;
      LoadError: string;
      Status: {
        Draft: string;
        Live: string;
        Expired: string;
        Unpublished: string;
      };
      Table: {
        Author: string;
        Text: string;
        State: string;
        LastPublishedAt: string;
        ExpiresAt: string;
        DateCreated: string;
        Publications: string;
        Actions: string;
        Preview: string;
        Publish: string;
        Republish: string;
        LiveUntil: string;
        Unpublish: string;
      };
      Messages: {
        PublishSuccess: string;
        PublishError: string;
        DeleteSuccess: string;
        DeleteError: string;
        DeleteConfirmTitle: string;
        DeleteConfirm: string;
        LockedWhileLive: string;
        UnpublishSuccess: string;
        UnpublishError: string;
        UnpublishConfirmTitle: string;
        UnpublishConfirm: string;
        NotLiveAnymore: string;
      };
      Form: {
        CreateTitle: string;
        EditTitle: string;
        SaveAndPublish: string;
        SaveDraft: string;
        Save: string;
        SaveSuccess: string;
        SaveError: string;
      };
    };
    PostStory: {
      StoryImage: string;
      StoryVideo: string;
      Text: string;
      ButtonLabel: string;
      ButtonUrl: string;
      SheetSection: string;
      SheetTitle: string;
      SheetDescription: string;
      SheetPrice: string;
      SheetPriceNote: string;
      SheetImage: string;
      Form: {
        Format: {
          Title: string;
          Subtitle: string;
          PhotoTitle: string;
          PhotoHint: string;
          VideoTitle: string;
          VideoHint: string;
        };
        Media: {
          PhotoTitle: string;
          PhotoSubtitle: string;
          VideoTitle: string;
          VideoSubtitle: string;
          PosterLabel: string;
          PosterHint: string;
        };
        Text: {
          Title: string;
        };
        Button: {
          Title: string;
          Subtitle: string;
          LabelPlaceholder: string;
          UrlPlaceholder: string;
        };
        Offer: {
          Subtitle: string;
          Attach: string;
          Remove: string;
        };
      };
      LimitExceeded: string;
      UsageLabel: string;
      Validation: {
        MediaRequired: string;
        ButtonUrlInvalid: string;
        ButtonLabelRequired: string;
      };
    };
  };
  Common: {
    TurnOn: string;
    LoadMore: string;
    BackToMoone: string;
    Retry: string;
    SaveError: string;
    DownloadError: string;
    Restore: string;
    TaxNumber: string;
    VatNumber: string;
    VatStatus: Record<VatStatus, string>;
    // The register's word on a party's VAT ID, shared by the onboarding step and the profile section.
    VatRegistryStatusLabel: string;
    VatRegistryStatus: string;
    VatRegistryUnavailableHint: string;
    VatNumberRejectedByRegistry: string;
    AnonymousUser: string;
    UnnamedChannel: string;
    SearchMinCharsHint: string;
    MoreOptions: string;
    Loading: string;
    ThankYou: string;
    Copy: string;
    Disable: string;
    Status: string;
    Balance: string;
    ComingSoon: string;
    Month: string;
    NotAvailable: string;
    Notes: string;
    Yes: string;
    User: string;
    Back: string;
    Next: string;
    Close: string;
    Open: string;
    Clear: string;
    Pause: string;
    Play: string;
    Mute: string;
    Unmute: string;
    No: string;
    AppName: string;
    Amount: string;
    TotalAmount: string;
    Note: string;
    MandatoryNoteDescription: string;
    Unknown: string;
    UnknownRecipient: string;
    ShowAll: string;
    ShowMore: string;
    ShowLess: string;
    ShowProfile: string;
    VariableSymbol: string;
    Required: string;
    InvalidIBAN: string;
    CancelEdit: string;
    Cancel: string;
    Confirm: string;
    UnsavedChangesTitle: string;
    UnsavedChangesWarning: string;
    DiscardChanges: string;
    NiceDay: string;
    Send: string;
    Continue: string;
    FillMissing: string;
    IncompleteSection: string;
    FirstName: string;
    LastName: string;
    Gender: string;
    PersonalNumber: string;
    BirthPlace: string;
    Nationality: string;
    OtherNationalities: string;
    Number: string;
    Type: string;
    IdentityDocument: string;
    Contact: string;
    Phone: string;
    PhoneVerified: string;
    PhoneVerificationDescription: string;
    Remove: string;
    Replace: string;
    Optional: string;
    Delete: string;
    Edit: string;
    Verify: string;
    BirthDate: string;
    AccountNumber: string;
    TransactionPaymentCode: string;
    IBAN: string;
    Save: string;
    Saved: string;
    DownloadQRCode: string;
    Email: string;
    Login: string;
    CompanyName: string;
    CompanyNumber: string;
    PartyTypeLabel: string;
    PartyType: Record<PartyType, string>;
    PartyTypeShortcut: Record<PartyType, string>;
    Sent: string;
    Price: string;
    License: string;
    Licenses: string;
    QRInstructions: string;
    QRPayment: string;
    ShareToBankApp: string;
    UnknownDate: string;
    Restart: string;
    Time: {
      TimePassed: WithPlurals<{
        Now: string;
        Minute: string;
        Hour: string;
        Day: string;
        Week: string;
        Month: string;
        Year: string;
      }>;
      TimeRemaining: WithPlurals<{
        Minute: string;
        Hour: string;
        Day: string;
        Week: string;
        Month: string;
        Year: string;
      }>;
    };
    GeneralInfo: string;
    RiskProfileStatus: Record<RiskProfileStatus, string>;
    UserType: Record<AccessRoleCode, string>;
    UnknownUserType: string;
    Unsupported: string;
    FacilityCategory: {
      Restaurants: string;
      Food: string;
      Car: string;
      Electronics: string;
      ClothingAndShoes: string;
      HealthAndBeauty: string;
      Finance: string;
      LeisureTime: string;
      PhoneTvAndInternet: string;
      Housing: string;
      Travel: string;
      Insurance: string;
      Education: string;
      Institutions: string;
      ConstructionAndMaintenance: string;
      ECommerceAndOnlineServices: string;
      PersonalServices: string;
      LegalAndConsultingServices: string;
      IndustrialProductionAndProcessing: string;
      Other: string;
    };
    WeekDay: Record<WeekDay, string>;
    Verification: {
      Verified: string;
      NotVerified: string;
    };
    Address: {
      Title: string;
      Type: Record<AddressType, string>;
      Street: string;
      StreetNumber: string;
      OrientationNumber: string;
      PostalCode: string;
      Municipality: string;
      NoAddressAvailable: string;
    };
    BeneficialOwner: {
      Title: string;
      Name: string;
      LastName: string;
      Gender: string;
      BirthDate: string;
      Nationality: string;
      IsForeigner: string;
    };
  };
  CodeLists: {
    IdentificationList: {
      IDCard: string;
      Passport: string;
      DrivingLicense: string;
      Other: string;
    };
    IdentityDocumentVerdict: {
      NotChecked: string;
      Approved: string;
      Declined: string;
      InReview: string;
      Unavailable: string;
    };
  };
  Errors: {
    DataLoadingError: string;
    RequestError: string;
    ApplicationError: {
      Title: string;
      Home: string;
      ErrorOccurred: string;
      TeamHasBeenInformed: string;
      AreYouDeveloper: string;
    };
    Error404: {
      Subtitle: string;
      Home: string;
    };
    Error403: {
      Subtitle: string;
    };
    NoUserRole: string;
    MissingId: string;
    UnknownError: string;
  };
  Offline: {
    Title: string;
    NoInternet: string;
    ServerUnreachable: {
      Title: string;
      Message: string;
    };
    Reconnected: string;
  };
  SignIn: {
    Title: string;
    Form: {
      Controls: {
        SignInButtonLabel: string;
        CancelButtonLabel: string;
        ForgottenPassword: string;
      };
      Fields: {
        Email: string;
        Password: string;
      };
    };
    Error: {
      General: string;
      Forbidden: string;
      InvalidLogin: string;
      InvalidCredentials: string;
      ApplicationVerification: string;
      PhoneNumberNotFound: string;
      SmsSendingFailed: string;
      InvalidToken: string;
      AccountNotFound: string;
      NotFound: string;
      AccountIsDeactivated: string;
      AccountLinkedToIdentity: string;
    };
  };
  SignUp: {
    Title: {
      Merchant: string;
      Customer: string;
      Hunter: string;
    };
    Form: {
      Controls: {
        SignUpButtonLabel: string;
      };
      Fields: {
        Login: string;
        Password: string;
        AgreeWithTermsAndConditions: {
          MooneLite: {
            Text: string;
            LinkText: string;
            LinkUrl: string;
          };
          FullMooneEULA: {
            StartText: string;
            ContractText: string;
            ContractUrl: string;
            MiddleText: string;
            RelatedDocsText: string;
            RelatedDocsUrl: string;
          };
          EULACheckboxLabel: {
            StartText: string;
            ContractText: string;
            ContractUrl: string;
            MiddleText: string;
            RelatedDocsText: string;
            RelatedDocsUrl: string;
          };
          HunterEULACheckboxLabel: {
            StartText: string;
            ContractText: string;
            ContractUrl: string;
            MiddleText: string;
            RelatedDocsText: string;
            RelatedDocsUrl: string;
          };
        };
      };
    };
    Error: {
      General: string;
      UserExists: string;
      WeakPassword: string;
    };
    AffiliateLandingDisclaimer: {
      Title: string;
      Body: string;
      SignInButton: string;
    };
  };
  SetPassword: {
    Error: {
      General: string;
      TokenNotFound: string;
      TokenExpired: string;
      TokenExpiredLinkText: string;
      WeakPassword: string;
    };
  };
  Validation: {
    InvalidLogin: string;
    Required: string;
    InvalidEmail: string;
    InvalidUrl: string;
    CompanyNumber: string;
    CompanyName: string;
    Password: {
      Required: string;
      MinLength: string;
      MinLowercase: string;
      MinUppercase: string;
      MinNumber: string;
      Match: string;
      MatchRequirement: string;
    };
    Phone: {
      InvalidFormat: string;
      MinDigits: string;
      MaxCharacters: string;
    };
    PersonalNumber: string;
    EulaAgreement: string;
    WalletAddress: string;
    InvalidPersonalNumber: string;
    OtherNationalitySameAsPrimary: string;
    InvalidVatNumber: string;
    InvalidBankAccount: string;
    InvalidPostalCode: string;
    ResidenceRequired: string;
    ExpirationAfterScheduled: string;
    DateMustBeInFuture: string;
    TimeMustBeInFuture: string;
    MaxLength: string;
  };
  ValidationMessages: {
    Required: string;
    ArrayMinOne: string;
    TooSmallString: string;
    TooBigString: string;
    InvalidEmail: string;
    InvalidUrl: string;
  };
  Menu: {
    BottomMenuItems: {
      Overview: string;
      History: string;
      InitiateKeypadPayment: string;
      ActionButton: string;
      Accounts: string;
      Settings: string;
      Licenses: string;
      Merchants: string;
      Users: string;
      NewMerchant: string;
      Stardust: string;
      Valor: string;
      Settlement: string;
      ThanksToYou: string;
      NotificationsManagement: string;
      ToBeVerified: string;
      Hunters: string;
      WhereToPay: string;
      Chat: string;
    };
    Help: string;
    Logout: string;
  };
  UserMenu: {
    ManageAccount: string;
    MooneLiteInfo: string;
    Invitations: string;
    Logout: string;
    Rewards: {
      TotalRewards: string;
      TotalStardust: string;
      TotalValor: string;
      MyShare: string;
      OpenRewardsDetails: string;
    };
    Notifications: {
      Title: string;
      NoNewNotifications: string;
      NoNotifications: string;
      Today: string;
      Yesterday: string;
      Older: string;
      ShowAll: string;
      DeleteAll: string;
    };
  };
  Onboarding: {
    Title: string;
    LoadingTitle: string;
    ErrorTitle: string;
    ProcessTitle: string;
    LicenseSelectionTitle: string;
    ManualLicensePaymentTitle: string;
    BeneficialOwnersTitle: string;
    BankAccountVerificationTitle: string;
    PoliticallyExposed: {
      Title: string;
      Alert: string;
    };
    MerchantOrCustomerDescription: {
      Welcome: string;
      IdentityVerificationRequired: string;
      MoonePaymentIdentity: string;
      PaymentIdentityDescription: string;
      LicensedPaymentInstitution: string;
      BankLevelIdentityVerification: string;
      DocumentsUsagePurpose: string;
      QuickProcessAndThankYou: string;
    };
    HunterDescription: {
      IdentityVerification: string;
      HCRMDescription: string;
      RemoteContract: string;
      DocumentSecurity: string;
      QuickProcess: string;
      Welcome: string;
      IdentityVerificationRequired: string;
      HCRMWorkEnvironment: string;
      RemoteContractVerification: string;
      VerificationPrinciple: string;
      QuickProcessAndWelcome: string;
    };
    Steps: {
      Documents: {
        ExistingDocumentsInfo: string;
        LoadingError: string;
        Processing: string;
        ProcessingFailed: string;
        FirstDocumentTitle: string;
        SecondDocumentTitle: string;
        SecondDocumentDescription: string;
        NoOwningPartyError: string;
        Declined: {
          Title: string;
          Description: string;
          Retry: string;
          Continue: string;
          AttemptsLeft: string;
          AttemptsExhausted: string;
          Reasons: {
            [key in DeclinedReason]: string;
          };
        };
        Forms: {
          DocumentTitle: string;
          DocumentDescription: string;
          FrontSide: string;
          BackSide: string;
          SecondDocumentFrontSide: string;
          SecondDocumentBackSide: string;
          SecondDocumentTypeLabel: string;
          SecondDocumentTypePassport: string;
          SecondDocumentTypeDrivingLicense: string;
          SecondDocumentTypeOther: string;
        };
        Validations: {
          ForegroundMissing: string;
          BackgroundMissing: string;
          SecondForegroundMissing: string;
          SecondBackgroundMissing: string;
          SecondDocumentTypeMissing: string;
        };
      };
      PersonalInformation: {
        Title: string;
        DataConfirmation: string;
        StardustParticipation: {
          Text: string;
          LinkText: string;
          LinkUrl: string;
        };
        ThanksToYouParticipation: {
          Text: string;
          LinkText: string;
        };
        SaveError: string;
        LoadingError: string;
        FormGroups: {
          PersonalInformation: string;
          Address: string;
          IdentityDocument: string;
        };
        DocumentValidity: {
          Generic: string;
          From: string;
          To: string;
        };
        DataMarkedAsIncorrect: string;
        ScanIncomplete: string;
        DocumentIssued: string;
        StreetAddress: string;
        ConfirmDataRequest: string;
        UseCompanyAddress: string;
        ValueLabels: {
          PersonalInfo: {
            FirstName: string;
            LastName: string;
            PersonalNumber: string;
            BirthPlace: string;
            Gender: string;
            Nationality: string;
          };
          AddressInfo: {
            StreetWithNumber: string;
            Municipality: string;
            PostalCode: string;
          };
          DocumentInfo: {
            Type: string;
            Number: string;
            Validity: string;
            Issued: string;
          };
        };
      };
      FinalStep: {
        SubmitButton: string;
        LoadingError: string;
        SuccessToast: string;
        Form: {
          HasNonEUResidence: string;
          HasNonEUNationality: string;
        };
      };
    };
    BusinessOnboarding: {
      Steps: {
        OwnerModal: {
          Title: string;
        };
        BusinessInformation: {
          LoadingError: string;
          SaveError: string;
          CompanyNumberAresMismatch: string;
          CompanyNumberAlreadyInUse: string;
          Title: string;
          RegionLabel: string;
          SlovakVatRegistrationNotice: string;
          FormGroups: {
            BasicInformation: string;
            Headquarters: string;
            Contact: string;
          };
        };
        BeneficialOwners: {
          Title: string;
          Question: string;
          SecondaryQuestion: string;
          Description: string;
          CheckboxTitle: string;
          CheckboxDescription: string;
          OwnersTitle: string;
          AddOwner: string;
          AddNextOwner: string;
          SaveError: string;
          UboChoice: {
            IAmOwner: string;
            IAmOwnerDescription: string;
            NotTheOnlyOwner: string;
            ManualEntry: string;
            ManualEntryDescription: string;
            Unknown: string;
            UnknownDescription: string;
          };
          FormGroups: {
            BasicInformation: string;
            Address: string;
          };
        };
        BankAccount: {
          Title: string;
          AvailableBanks: string;
          BanksComingSoon: string;
        };
        FacilityCompletion: {
          Title: string;
          Description: string;
          IncompleteFacilitiesWarning: string;
          OrbitLFacilityRequiredWarning: string;
          ContinueWithoutFacility: string;
        };
        LicenseSelection: {
          Title: string;
          Description: string;
          AlreadyOwned: string;
          Form: {
            PriceIncludesVAT: string;
            ChooseLicense: string;
            WalletCheckboxLabel: string;
            PrivateWalletAddress: string;
            PrivateWalletAddressDescription: string;
            NoLicensesAvailableForCountry: string;
            SelectCountryFirst: string;
            SubmitButton: string;
            SeePaymentDetails: string;
            AlreadyOrdered: string;
          };
        };
        IncomeInformation: {
          Title: string;
          SaveError: string;
          FormGroups: {
            Business: string;
            Personal: string;
          };
          Fields: {
            BusinessActivity: string;
            OtherBusinessActivity: string;
            SourceOfIncome: string;
            OtherSourceOfIncome: string;
            RelationType: string;
            IsPoliticallyExposedPerson: string;
            HasNonEUResidence: string;
            HasNonEUNationality: string;
            ExpectedMonthlyRevenue: string;
            AverageTransactionValue: string;
            TaxResidencyCountry: string;
          };
        };
      };
    };

    BanksInPreparation: string;
    PotentialYearlySavings: string;
  };
  Transactions: {
    Title: string;
    History: string;
    NoTransactions: string;
    CancelTransaction: string;
    RefundTransaction: string;
    InverseTransactionCreated: string;
    TransactionCancelError: string;
    Payment: {
      Incoming: string;
      Outgoing: string;
      Unknown: string;
    };
    Type: {
      Buy: string;
      Sell: string;
      Withdrawal: string;
      LeadReward: string;
      LeadRewardConsumer: string;
      Voucher: {
        GiftCard: string;
        SignUpSubsidy: string;
      };
      Coinback: string;
    };
    ErrorPayment: {
      Title: string;
      TitleNotCompleted: string;
      Description: string;
    };
    SuccessPayment: {
      Title: string;
      DescriptionFirstLine: string;
      DescriptionSecondLine: string;
    };
    Anonymous: {
      DownloadReceipt: string;
      FillEmail: string;
      SignupTitle: string;
      Benefits: string;
      CampaignSignupTitle: string;
      CampaignSignupDescription: string;
      CreateAccountViaEmail: string;
      JustWantReceipt: string;
      BackToSignup: string;
      LoadingSkeleton: {
        loadingInfo: {
          title: string;
          description: string;
        };
        processingTransaction: {
          title: string;
          description: string;
        };
      };
    };
    Status: {
      Completed: string;
      PendingTrade: string;
      PendingPayout: string;
      WithdrawRequested: string;
      Withdrawn: string;
    };
    TransactionStatus: { [key in TransactionStatus]: string };
    RefundState: {
      Title: string;
      Refunded: string;
      Pending: string;
    };
    TransactionDirection: {
      Incoming: string;
      Outgoing: string;
      IncomingRefund: string;
      OutgoingRefund: string;
      Unknown: string;
    };
    BankTransferInfo: string;
    TransactionStatusDescription: { [key in TransactionStatus]: string };
    ReceiptOnly: string;
  };
  Notifications: {
    Variants: { [key in NotificationVariant]: string };
  };
  BankIDVerification: {
    Error: {
      General: string;
      BankIDUserInfoError: string;
      VerificationAlreadyExists: string;
    };
  };
  Voucher: {
    ApplyError: {
      General: string;
      NotFound: string;
      Applied: string;
      UserAccountNotFound: string;
      UserNotFound: string;
      UserIsWaitingForEmailVerification: string;
      InvalidRecaptcha: string;
    };
    ToOverview: string;
    SignIn: string;
    AgreeWith: string;
    Continue: string;
    CardCodeInstructions: string;
  };
  Balance: {
    Title: string;
    BalanceContent: WithPlurals<{
      Title: string;
      PayMeAction: string;
      SendPaymentAction: string;
      HiddenBalance: string;
      ShowBalance: string;
      HideBalance: string;
      CurrencyCount: string;
      CurrencySheetTitle: string;
    }>;
  };
  Banking: {
    Auth: {
      Title: string;
      Description: string;
      BankList: {
        Title: string;
        ComingSoon: string;
      };
      BankConnectionInfo: string;
      BankConnectionWarning: string;
    };
    AccountSelection: {
      Title: string;
      Description: string;
      DeleteLaterInfo: string;
      Cancel: string;
      Confirm: string;
      AccountsSetUp: string;
      RemoveError: string;
      SelectedAccounts: string;
      AllAccounts: string;
      WarningMessage: string;
    };
    BanksInPreparation: string;
    QRPaymentForeignBank: string;
    BatchPayments: {
      Title: string;
      DetailTitle: string;
      EmptyList: string;
      NotFound: string;
      LoadMore: string;
      Account: string;
      TotalAmount: string;
      PaymentCount: string;
      CreatedAt: string;
      AuthorizedAt: string;
      CompletedAt: string;
      SignInBank: string;
      PaymentsTable: string;
      Columns: {
        Date: string;
        Status: string;
        Account: string;
        PaymentCount: string;
        Total: string;
        Recipient: string;
        IBAN: string;
        VS: string;
        Amount: string;
      };
      Status: {
        Created: string;
        Initiated: string;
        Processing: string;
        Success: string;
        Fail: string;
        Cancelled: string;
      };
    };
  };
  BankNames: { [key in Bank]: string };
  BankEnvironmentNames: { [key in BankEnvironment]: string };
  BankRating: {
    Title: string;
    Description: string;
    AccessibilityLabel: string;
    ContactUs: {
      PreText: string;
      Email: string;
      PostText: string;
    };
    Detail: {
      CurrentRating: string;
      Titles: {
        Warning: string;
        HowToConnect: string;
        Review: string;
        Email: string;
      };
    };
    NotRecommended: {
      Badge: string;
      InfoboxTitle: string;
      BetterButton: string;
    };
    Banks: {
      [key in Bank]: {
        Rating: string;
        AdditionalInfo: string;
        Warnings: string;
        HowToConnect: string;
        Review: string;
        Email: string;
        NotRecommended?: {
          Reason: string;
          WarningText: string;
          RiskItButton: string;
        };
      };
    };
  };
  Hunter: {
    Dashboard: {
      UnderConstruction: string;
      MissingLicenseHolderBankAccount: string;
      HunterPortalBanner: {
        Title: string;
        Description: string;
      };
      Prerequisites: {
        Title: string;
        Description: string;
        Completed: string;
        GreatWork: string;
        InfoMessage: string;
        CompletedMessage: string;
        Items: {
          RegisterAsMerchant: string;
          InviteUserViaAffiliate: string;
          BanksAndPayments: string;
          OfflineTraining: string;
          OfflineTrainingLink: string;
          OfflineTrainingUrl: string;
          BringMerchantsWithHunter: string;
        };
      };
    };
    LicensePaymentPending: {
      Title: string;
      ProcessingInfo: string;
      NotPaidYetLink: string;
      NotPaidYetSubtitle: string;
      PaymentDetailsTitle: string;
      NoLicenseTitle: string;
      NoLicenseInfo: string;
      OrderLicense: string;
      OrderAnotherLicense: string;
      SelectedLicenseLabel: string;
      BackToMoone: string;
    };
    Merchants: {
      Title: string;
      AddNew: string;
      CompleteRegistration: {
        MerchantCompany: {
          LoadingError: string;
        };
        ContactPerson: {
          Title: string;
          OptionalTitle: string;
          OptionalNoItems: string;
        };
        GeneralTitle: string;
        Title: string;
        LoadingError: string;
        MerchantError: string;
        SaveMerchantPartyError: string;
        SaveMerchantPartySuccess: string;
        Steps: {
          party: string;
          notes: string;
          facilities: string;
          company: string;
          products: string;
          fees: string;
          completion: string;
        };
        Facilities: {
          RequiredInfo: string;
          ContinueWithoutFacility: string;
        };
        NextStep: string;
        PreviousStep: string;
        Notes: {
          SaveSuccess: string;
          SaveError: string;
          Title: string;
          Label: string;
          NoNotes: string;
        };
        SendAccess: {
          Title: string;
          MandatoryStepsTitle: string;
          Login: string;
          LoginDescription: string;
          SendAccessButton: string;
          BackOfficeTarget: string;
          MerchantAlreadyExists: string;
          MerchantAlreadyHasAccessToThisCompany: string;
          ExistingIdentityHint: string;
          NewIdentityHint: string;
          CompanyNumberAlreadyRegistered: string;
          SendAccessSuccess: string;
          SendAccessError: string;
          TestMerchantBannerTitle: string;
          TestMerchantBanner: string;
          Todos: {
            BasicInfo: string;
            BeneficialOwners: string;
            ContactPerson: string;
            Facilities: string;
            Pricing: string;
            HunterPrerequisites: string;
          };
        };
      };
      OpenRegistrations: {
        Title: string;
        LoadingError: string;
        RegisterNewMerchant: string;
        Table: {
          Title: string;
          Columns: {
            CompanyName: string;
            CompanyNumber: string;
            Phone: string;
            Email: string;
          };
        };
      };
      NewRegistration: {
        Title: string;
        StartRegistrationButton: string;
        StartRegistrationError: string;
        StartRegistrationSuccess: string;
        CreateMerchantError: string;
        Form: {
          Legend: {
            License: string;
            CommissionSharing: string;
          };
          Fields: {
            Country: string;
            Region: string;
            CompanyNumber: string;
            ConfirmationLabel: string;
            License: string;
            RemoveHunter: string;
            CommissionSettings: string;
            ShareCommissionWithHunters: string;
            ShareCommissionWithHuntersButton: string;
          };
          NoLicenseForRegion: string;
          StatusIndicator: {
            Loading: string;
            EnterCompanyNumber: string;
            CompanyAvailable: string;
            CompanyNotAvailable: string;
            CompanyNotFound: string;
          };
        };
      };
      Detail: {
        ActivateButton: string;
        ActivateSuccess: string;
        ActivateError: string;
        ResendAccessButton: string;
        ResendAccessSuccess: string;
        ResendAccessSuccessWithUser: string;
        ResendAccessError: string;
        CancelAccessButton: string;
        CancelAccessConfirmTitle: string;
        CancelAccessConfirmBody: string;
        CancelAccessSuccess: string;
        CancelAccessError: string;
        CancelAccessForbidden: string;
        Tabs: {
          Merchant: string;
          Products: string;
          TerminalContracts: string;
          Paypoints: string;
          Settlements: string;
        };
      };
      ListPage: {
        LoadingError: string;
        LicenseError: string;
        LicenseNotActive: string;
        ShowLicenses: string;
        MerchantsTitle: string;
        SharedMerchantsTitle: string;
        SharedMerchantsError: string;
        Table: {
          Columns: {
            MerchantStatus: string;
            ContactPerson: string;
            Party: {
              CompanyName: string;
              Phone: string;
              Email: string;
              SettlementStatus: string;
            };
            LicenseNumber: string;
            SharePercentage: string;
            TransactionsCount: string;
            DaysRegistered: string;
          };
        };
      };
    };
    AllMerchants: {
      LoadingError: string;
      OwnedByMe: string;
      TableHeaders: {
        Status: string;
        CompanyNumber: string;
        CompanyName: string;
        LicenseOwner: string;
        LicenseType: string;
        DateCreated: string;
        TransactionsCount: string;
      };
    };
    UnassignedMerchants: {
      Description: string;
      LoadingError: string;
      EmptyState: string;
      OpenDetailLabel: string;
      TableHeaders: {
        Status: string;
        CompanyNumber: string;
        CompanyName: string;
        DateCreated: string;
        Detail: string;
      };
      AssignDialog: {
        Title: string;
        Subtitle: string;
        PrimaryLicenseLabel: string;
        MainCommission: string;
        ShareLegend: string;
        ShareCommissionWithHuntersButton: string;
        AssignButton: string;
        AssignWarning: string;
        CancelButton: string;
        AssignSuccess: string;
        AlreadyHasHunterError: string;
        GenericError: string;
        Validation: {
          PrimaryRequired: string;
          ShareOutOfRange: string;
          PrimaryUnderMin: string;
        };
      };
    };
    HuntersOverview: {
      LoadingError: string;
      Detail: {
        UserInfo: string;
        PartyInfo: string;
        ContactInfo: string;
        Login: string;
        Status: string;
        FirstName: string;
        LastName: string;
        BirthDate: string;
        CompanyName: string;
        CompanyNumber: string;
        TaxNumber: string;
        Email: string;
        Phone: string;
        Licenses: string;
        NoLicensesFound: string;
        AffiliatedTo: string;
        EditUser: string;
        DateCreated: string;
        PartyType: string;
      };
      TableHeaders: {
        Login: string;
        FirstName: string;
        LastName: string;
        CompanyName: string;
        DateCreated: string;
        Status: string;
        AffiliatedTo: string;
        Prerequisites: string;
      };
    };
  };
  Transfer: {
    FromAccount: string;
    ToAccount: string;
    Amount: string;
    Note: string;
    SubmitButton: string;
    TransferButton: string;
    ConnectMoreAccounts: string;
    NoBankAccountsError: string;
  };
  Merchant: {
    Employees: {
      Title: string;
    };
    LicenseShares: {
      Title: string;
    };
    Payment: {
      Title: string;
      InitiatePaymentButton: string;
      InitiateKeypadPaymentButton: string;
      LoadingError: string;
    };
    Initiate: {
      GenerateButton: string;
      ConnectBankAccountLink: {
        PreText: string;
        LinkText: string;
      };
      PaypointHasNoAccountError: string;
      NoBankAccountsError: string;
      ActiveTransactionConflict: {
        Title: string;
        Message: string;
        Description: string;
        OverwriteButton: string;
        Overwriting: string;
      };
      Form: {
        BusinessAccountSelectTitle: string;
        PersonalAccountSelectTitle: string;
      };
    };
    InitiateDetail: {
      MerchantInstructions: string;
      CustomerInstructions: string;
      QRCodeInstructionsSuccess: string;
      QRCodeWarning: string;
      UsePaypointWarning: string;
      QRInstructions: string;
      PaymentStatus: string;
      TransactionFee: string;
      ProgressHeader: Record<TransactionStatus, string>;
      ActionButtons: {
        Share: string;
        Info: string;
        Qr: string;
      };
      ShareTitle: string;
      ShareText: string;
      ShareReceiptTitle: string;
      ShareReceiptText: string;
      NFCInstruction: string;
      Error: string;
      Loading: string;
      CreateAnotherPayment: string;
      BackToOverview: string;
      ThankYouMessage: string;
    };
    MerchantStatus: { [key in MerchantStatus]: string };
    NoProductAssigned: string;
    NoPaypointProduct: string;
    DataFailedToLoad: string;
    PaymentDetails: string;
    AccountNumber: string;
    VariableSymbol: string;
    IBAN: string;
    PaymentReference: string;
    Amount: string;
    Note: string;
    NoAccountSet: string;
    ConnectedBankAccounts: string;
  };
  Customer: {
    Pay: {
      Title: string;
      DescriptionText: string;
      PayButton: string;
      PayButtonWithAmount: string;
      ChooseBankAccount: string;
      ChooseBank: string;
      AddNextBankAccount: string;
      MooneLiteAccountExplanation: string;
      SelectBankAccount: string;
      TransactionIdentifier: string;
      Amount: string;
      BillAmount: string;
      Tip: string;
      AddTip: string;
      fivePercent: string;
      tenPercent: string;
      twentyPercent: string;
      custom: string;
      TipContinueWithout: string;
      TipCustomAmountLabel: string;
      BankAccount: string;
      QuickPayment: {
        Processing: string;
        Error: string;
        QuickPaymentFailed: string;
        UseAnotherAccount: string;
      };
      AnonymousCollector: {
        UnsupportedMode: string;
      };
      ManualEntry: {
        Title: string;
        NfcTitle: string;
        NfcSubtitle: string;
        ManualTitle: string;
        ManualTitleWithNfc: string;
        ManualSubtitle: string;
        InputPlaceholder: string;
        InputLabel: string;
        SubmitButton: string;
        InvalidId: string;
        TransactionIdNotProvided: string;
      };
    };
  };
  BankAccounts: {
    List: {
      Description: string;
      NoAccounts: string;
      SaveOrder: string;
      EditOrder: string;
      AddAccount: string;
    };
    VerificationStatus: {
      Inactive: string;
      AwaitingVerification: string;
      Rejected: string;
      Active: string;
      Restricted: string;
      PreApproved: string;
    };
    VerificationStatusDescription: {
      Active: string;
      AwaitingVerification: string;
      Restricted: string;
      Inactive: string;
      Rejected: string;
      PreApproved: string;
    };
    BankVerificationSource: {
      AIS: string;
      QR: string;
    };
  };
  Form: {
    PersonalData: {
      Sex: {
        Female: string;
        Male: string;
        NotSet: string;
      };
    };
    PartyType: { [key in PartyType]: string };
    BusinessActivity: { [key in BusinessActivity]: string };
    SourceOfIncome: { [key in SourceOfIncome]: string };
    SourceOfIncomeHint: {
      GoodsSalesRevenue: string;
      ServiceAndCraftIncome: string;
      HospitalityAndAccommodation: string;
      CommissionsAndLicensing: string;
      PropertyAndInvestmentIncome: string;
      PublicBudgetsAndGrants: string;
      OtherBusinessIncome: string;
    };
    ExpectedMonthlyRevenue: { [key in ExpectedMonthlyRevenue]: string };
    AverageTransactionValue: { [key in AverageTransactionValue]: string };
    RelationType: { [key in RelationType]: string };
    RoleCategory: {
      StatutoryBody: string;
      StatutoryBodyHint: string;
      ProxyHolder: string;
      Other: string;
      SpecificRole: string;
    };
    BankVerification: { [key in BankVerificationStatus]: string };
    BankVerificationSource: { [key in BankVerificationSource]: string };
    IdentityVerification: { [key in IdentityVerificationStatus]: string };
    AdditionalInfo: { [key in AdditionalInfoStatus]: string };
    UserStatus: { [key in UserStatus]: string };
    IdentityUserStatus: { [key in IdentityUserStatus]: string };
    AddressType: { [key in AddressType]: string };
    Version: { [key in Version]: string };
  };
  FormSelectors: {
    BusinessForm: {
      Title: string;
    };
  };
  User: {
    AccessRole: Record<AccessRoleCode, string>;
    RoleFilter: {
      CustomerOnly: string;
    };
  };
  ApiStatuses: {
    UnknownError: string;
    UploadIdentificationCommandResultStatus: {
      [key in UploadIdentificationCommandResultStatus]: string;
    };
    UploadIdentificationCommandResultStatus2: {
      [key in UploadIdentificationCommandResultStatus2]: string;
    };
  };
  System: {
    Addresses: string;
    BlockchainFee: string;
    MempoolFeeLevel: string;
    Exchange: string;
    Trades: string;
    Moone: string;
    HotWallet: string;
    ColdWallet: string;
    ExchangeWithdrawal: string;
    UserAccounts: string;
    ControlSum: string;
    ExchangeWithdrawalFee: string;
    TradingVolume: string;
    PerMonth: string;
    PerWeek: string;
    Per24h: string;
    PurchasedBTC: string;
    WithdrawnOrPending: string;
    BTCLedgerSum: string;
    CZKLedgerSum: string;
  };
  Error: {
    Page403: string;
    Page404: string;
    Unexpected: string;
    NoAccess: string;
    OpenRegistrationsFailed: string;
    MerchantNotSelected: string;
    LicenseInfoFailed: string;
    InvalidPaypointId: string;
    SomethingWentWrong: string;
    InvalidUserRole: string;
    GoogleLoginFailed: string;
    Error: string;
    ProgrammerHint: string;
    AccountsFailed: string;
    UserDataFailed: string;
    VerificationDataFailed: string;
    DocumentsFailed: string;
    PersonDataFailed: string;
    IncomeDataFailed: string;
    FormPreparationFailed: string;
    RelatedPartyDataFailed: string;
    MerchantFacilitiesFailed: string;
    UserFailed: string;
    ActingPersonFailed: string;
    DocumentsFailedToLoad: string;
    AffiliateCodeFailed: string;
    NoInvitationsSent: string;
    QRPaymentFailed: string;
  };
  Dashboard: {
    NoAccess: string;
    NoAccessRetry: string;
    AlreadyHaveAccount: string;
    EmptyForRole: string;
    IncomeTypeQuestion: string;
    Back: string;
    Loading: string;
  };
  Auth: {
    Redirecting: string;
    Error: string;
    StateSync: {
      LoggedInFromAnotherTab: string;
      LoggedOutFromAnotherTab: string;
    };
    AuthPageLayout: {
      DividerText: string;
      SignInLinkText: string;
      SignUpLinkText: string;
      SignInPrompt: string;
      SignUpPrompt: string;
    };
    SSO: {
      CompleteSSORegistration: {
        Welcome: string;
        Title: string;
        Description: string;
        SignUpAs: string;
        RoleDescription: {
          Merchant: string;
          Customer: string;
          Hunter: string;
        };
        RoleLabel: {
          Merchant: string;
          Customer: string;
          Hunter: string;
        };
      };
      Google: {
        ButtonLabel: string;
      };
      Apple: {
        ButtonLabel: string;
        SignInFailed: string;
        NotSupportedInApp: string;
      };
      BankID: {
        ButtonLabel: string;
      };
    };
    Migration: {
      Title: string;
      Welcome: string;
      Description: string;
      RoleHint: string;
      LinkToCustomerOption: string;
      CreateIdentityOption: string;
      CreateIdentityDeferred: string;
      LinkToCustomerTitle: string;
      LinkToCustomerDescription: string;
      LoginLabel: string;
      PasswordLabel: string;
      LinkButton: string;
      BackButton: string;
      LinkError: string;
      LinkSsoDivider: string;
      LinkSsoDescription: string;
      LinkSsoNoAccount: string;
      LinkSsoError: string;
      CreateError: string;
      AccountNotReady: string;
      EmployeeNotSupported: string;
      BackOfficeNotSupported: string;
      SignOut: string;
      MerchantCreateIdentity: {
        Title: string;
        Description: string;
        CompanyLabel: string;
        CompanyNumber: string;
        UnknownCompany: string;
        PickPersonTitle: string;
        AlreadyLinkedHint: string;
        UnnamedPerson: string;
        StatutoryDeclaration: string;
        CreateAsButton: string;
        CreateAsButtonNoSelection: string;
        ManualEntryToggle: string;
        ManualEntryToggleEmpty: string;
        ManualTitle: string;
        FirstNameLabel: string;
        LastNameLabel: string;
        BirthDateLabel: string;
        PhoneLabel: string;
        SubmitManualButton: string;
        SubmitError: string;
        LoadError: string;
        RelationTypes: Record<
          BusinessRelationType | "BeneficialOwner" | "ContactPerson",
          string
        >;
      };
    };
    Invitations: {
      PageTitle: string;
      PageDescription: string;
      EmptyState: string;
      MerchantInviteTitle: string;
      MerchantInviteUnnamedCompany: string;
      EmployeeInviteTitle: string;
      EmployeeBellTitle: string;
      EmployeeBellMessage: string;
      MerchantBellTitle: string;
      MerchantBellMessage: string;
      InvitedBy: string;
      ExpiresAt: string;
      AcceptButton: string;
      DeclineButton: string;
      KycRequired: string;
      AcceptError: string;
      AcceptedButSwitchFailed: string;
      DeclineError: string;
      LoadError: string;
    };
    ContextSwitcher: {
      Title: string;
      AddRole: string;
      Switching: string;
      PersonalAccountHint: string;
      Roles: Record<AccessRoleCode, string>;
    };
    ForgottenPassword: {
      Title: string;
      Email: string;
      ResetPasswordButton: string;
      SuccessMessage: string;
    };
    EmailVerification: {
      EmailSentTo: string;
      Redirecting: string;
      EmailVerification: string;
      EmailSent: string;
      ResendEmail: string;
      EmailInstructions: string;
      ResendEmailLabel: string;
      ResendEmailText: string;
    };
  };
  Account: {
    ThankYouFriendInvited: string;
  };
  Components: {
    PasswordInput: {
      ShowPassword: string;
      HidePassword: string;
    };
    EmployeeAutocomplete: {
      Placeholder: string;
      SelectAll: string;
    };
    CategoryAutocomplete: {
      Placeholder: string;
    };
    LanguageSwitcher: {
      ComingSoon: string;
    };
    CountrySelector: {
      LoadingError: string;
      Label: string;
      ComingSoon: string;
      SlovakLicensesComingSoon: string;
    };
    RegionRequiredModal: {
      Title: string;
      Description: string;
      LoadingError: string;
      Error: string;
      Confirm: string;
    };
    LicensePayment: {
      PendingActivationInfo: string;
    };
    LicenseOrder: {
      CreateError: string;
      CreateSuccess: string;
      LoadingError: string;
      Coupon: {
        Title: string;
        CheckboxLabel: string;
        Label: string;
        Placeholder: string;
        Validate: string;
        Applied: string;
        Remove: string;
        OriginalPrice: string;
        Discount: string;
        FinalPrice: string;
        Error: {
          CouponNotFound: string;
          CouponExpired: string;
          CouponOutOfStock: string;
          CouponInvalidType: string;
          CouponRegionMismatch: string;
          Unavailable: string;
        };
      };
    };
    Invoice: {
      PaymentData: string;
      AccountNumber: string;
      VariableSymbol: string;
      SpecificSymbol: string;
      IBAN: string;
      Reference: string;
      Amount: string;
      Note: string;
      MandatoryNoteDescription: string;
      EmployerGuidelineButton: string;
      BulkDownload: {
        Download: string;
        Success: string;
        Error: string;
      };
    };
    ShareButton: {
      AccessibilityLabel: string;
      Error: string;
    };
    LicensePicker: {
      LoadingError: string;
      LicenseDescription: Record<LicenseCode, string>;
      Perks: WithPlurals<{
        Terminals: string;
        SubAccounts: string;
      }> & {
        NoSubAccounts: string;
      };
    };
    BanksList: {
      DataLoadingError: string;
      ShowAll: string;
      HideAll: string;
    };
    Bitcoin: string;
    IBAN: string;
    Maximum: string;
    SAT: string;
    Or: string;
    RateConfirmation: string;
    MooneLitePopup: {
      Title: string;
      Message: string;
      BankVerificationMessage: string;
      CompleteRegistrationButton: string;
      ConnectBankAccountButton: string;
      LimitExceeded: string;
      RestrictionList: {
        NoPeerToPeerPayments: string;
        NoBalance: string;
        NoIncomingPayments: string;
        NoLoyaltyPoints: string;
        MaxPaymentLimit: string;
        MaxTransactionsLimit: string;
      };
    };
    SetupChecklist: {
      Title: string;
      Subtitle: string;
      LiteSubtitle: string;
      MerchantTitle: string;
      MerchantSubtitle: string;
      StepsCompleted: string;
      Start: string;
      InReview: string;
      Steps: {
        ConnectBank: {
          Title: string;
          Description: string;
          Action: string;
        };
        SetPrimaryAccount: {
          Title: string;
          Description: string;
          Action: string;
          BlockedDescription: string;
        };
        VerifyIdentity: {
          Title: string;
          Description: string;
          Action: string;
        };
      };
    };
    RevalidateBankingAuthModal: {
      Title: string;
      Description: string;
      InvalidTitle: string;
      InvalidDescription: string;
      InvalidPispDescription: string;
      ExpiringPispTitle: string;
      ExpiringPispDescription: string;
    };
    UnpaidSettlementPopup: {
      PayMonthlySettlements: string;
      PayMonthlySettlement: string;
      NoSettlementTransactionError: string;
    };
    LimitedAccountPopup: {
      Title: string;
      Message: string;
    };
    DocumentExpirationWarning: {
      UploadNewDocument: string;
      UploadSuccess: string;
      AdminWarningTitle: string;
      AdminWarningMessage: string;
      AdminExpiredTitle: string;
      AdminExpiredMessage: string;
      WarningPrimaryTitle: string;
      WarningPrimaryMessage: string;
      ExpiredPrimaryTitle: string;
      ExpiredPrimaryMessage: string;
      WarningSecondaryTitle: string;
      WarningSecondaryMessage: string;
      ExpiredSecondaryTitle: string;
      ExpiredSecondaryMessage: string;
      AdminWarningSecondaryTitle: string;
      AdminWarningSecondaryMessage: string;
      AdminExpiredSecondaryTitle: string;
      AdminExpiredSecondaryMessage: string;
      SecondDocumentValidTo: string;
      AdminNotificationExpiredTitle: string;
      AdminNotificationExpiredMessage: string;
    };
    RelatedPartyDocumentExpiration: {
      Title: string;
      WarningMessage: string;
      ExpiredMessage: string;
      UpdateDocuments: string;
    };
    NoPrimaryAccountPopup: {
      Message: string;
      Accounts: string;
    };
    Account: {
      Products: string;
      NoRoleAssignedError: string;
      Primary: string;
      VerificationAccount: string;
      AccountOwner: string;
      ConnectionDate: string;
      ManualVerificationRequired: string;
      ActivateAccount: string;
      RejectAccount: string;
      AccountActivationConfirm: string;
      AccountRejectionConfirm: string;
      TerminalDocuments: string;
      TerminalDocumentsFailed: string;
      MerchantFacilitiesFailed: string;
      RelatedParties: string;
      RelatedPartySaved: string;
      RelatedPartyError: string;
      NoDocumentsForPerson: string;
      VerificationSaved: string;
      VerificationError: string;
      GeneralInformation: string;
      Verification: string;
      Company: string;
      ContactPersons: string;
      Facilities: string;
      Declaration: string;
      PersonalInformation: string;
      CompanyName: string;
      TaxNumber: string;
      TaxResidence: string;
      BusinessSubject: string;
      SpecifyBusinessSubject: string;
      IncomeSource: string;
      LegacyIncomeSource: string;
      SpecifyIncomeSource: string;
      ExpectedMonthlyVolume: string;
      AverageTransactionValue: string;
      PoliticallyExposed: string;
      NonEUPermanentResidence: string;
      NonEUNationality: string;
      StreetNumber: string;
      OrientationNumber: string;
      PostalCode: string;
      DocumentNumber: string;
      BankAccount: string;
      PartyError: string;
      VatRegistration: string;
      VatNumberRestoredByRegistry: string;
      VatStatusNotVerifiedHint: string;
      PersonalDataChangeNotification: string;
      SecondDocumentUploadError: string;
      SecondDocumentUploadSuccess: string;
      FirstDocumentUploadError: string;
      FirstDocumentUploadSuccess: string;
      FirstDocumentUpload: {
        UploadButton: string;
        FileDescription: string;
      };
      SecondDocumentUpload: {
        Title: string;
        Description: string;
        UploadButton: string;
        FileLabel: string;
        FileDescription: string;
      };
      DeactivateUser: {
        Title: string;
        Description: string;
        Confirmation: string;
        DeactivateButton: string;
        SuccessMessage: string;
        InfoSection: {
          Title: string;
          Paragraph1: string;
          Paragraph2: string;
        };
      };
      AccountTab: string;
      PublicInformationTab: string;
    };
    Forms: {
      Validation: {
        InvalidEmail: string;
        PasswordMinLength: string;
        PasswordLowercase: string;
        PasswordUppercase: string;
        PasswordNumber: string;
        TermsAgreement: string;
        UserAlreadyExists: string;
        InvitationAlreadySent: string;
        InvitationError: string;
      };
    };
    Merchant: {
      LoadingError: string;
      PaymentFailed: string;
      AccountLoadingError: string;
      PayPoints: {
        NoProductAssigned: string;
        NoPaypointProduct: string;
        NoBankAccountConnectedWarning: {
          title: string;
          linkPretext: string;
          linkText: string;
        };
      };
      Logo: {
        FacilityTitle: string;
        Hint: string;
        FileTooLarge: string;
        InvalidFileType: string;
        UploadSuccess: string;
        UploadError: string;
        DeleteSuccess: string;
        DeleteError: string;
      };
      Avatar: {
        Title: string;
        Hint: string;
        RemoveConfirm: string;
        LockedAfterOnboardingHint: string;
        UploadSuccess: string;
        UploadError: string;
        DeleteSuccess: string;
        DeleteError: string;
      };
      ContactPersons: {
        Title: string;
        LoadingError: string;
        DeleteSuccess: string;
        DeleteError: string;
        DeleteConfirmation: string;
        AddContactPerson: string;
        NewContactPerson: string;
        ContactPersonDetail: string;
        NoContactPersons: string;
        Form: {
          AddSuccess: string;
          AddError: string;
          UpdateSuccess: string;
          UpdateError: string;
          Legend: string;
        };
      };
      BeneficialOwners: {
        Title: string;
        LoadingError: string;
        NoBeneficialOwners: string;
        AddBeneficialOwner: string;
        NewBeneficialOwner: string;
        BeneficialOwnerDetail: string;
        Form: {
          DeleteConfirmation: string;
          AddSuccess: string;
          AddError: string;
          UpdateSuccess: string;
          UpdateError: string;
          Legend: string;
        };
      };
      Facilities: {
        Title: string;
        LoadingError: string;
        DeleteSuccess: string;
        DeleteError: string;
        DeleteConfirmation: string;
        AddFacility: string;
        NewFacility: string;
        EditFacility: string;
        FacilityDetail: string;
        NoFacilities: string;
        NoAddress: string;
        Incomplete: string;
        NoPhotos: string;
        PhotosLoadingError: string;
        Form: {
          AddSuccess: string;
          AddError: string;
          UpdateSuccess: string;
          UpdateError: string;
          PhotoUploadSuccess: string;
          PhotoUploadError: string;
          PhotoTooLarge: string;
          PhotoInvalidType: string;
          PhotosNotSaved: string;
          PhotosDescription: string;
          UploadPhotos: string;
          PhotoSelected: string;
          PhotoUploadButton: string;
          PhotoChangeButton: string;
          PhotoDeleteButton: string;
          Legend: {
            BasicInformation: string;
            Contact: string;
            OpeningHours: string;
            Address: string;
            Turnover: string;
            Terminals: string;
            PublicFacilityInfo: string;
            DetailedOpeningHours: string;
            Photos: string;
          };
          RequiredSection: string;
          OptionalSection: string;
          OptionalSectionDescription: string;
          PublicFacilityDescription: string;
          IsPublicHelpText: string;
          UseRegisteredAddress: string;
          PrefillContact: string;
          NoName: string;
          Validation: {
            CategoryRequiredWhenPublic: string;
          };
          Fields: {
            Name: string;
            ProvidedServices: string;
            ResponsiblePerson: string;
            Phone: string;
            Email: string;
            IsPublic: string;
            Category: string;
            OpeningHoursFrom: string;
            OpeningHoursTo: string;
            Address: {
              Street: string;
              StreetNumber: string;
              OrientationNumber: string;
              PostalCode: string;
              Municipality: string;
              District: string;
            };
            WeekDays: {
              Monday: string;
              Tuesday: string;
              Wednesday: string;
              Thursday: string;
              Friday: string;
              Saturday: string;
              Sunday: string;
            };
            YearlyTurnover: string;
            RealCardTurnover: string;
            AverageTransactionValue: string;
            EstimatedTurnover: string;
            EstimatedTurnoverHelperText: string;
            TerminalCount: string;
            TerminalCountHelperText: string;
            SoftPosCount: string;
            SoftPosCountHelperText: string;
            Description: string;
            PhotoOutside: string;
            PhotoInside: string;
          };
          OpeningHours: {
            Title: string;
            ClosedAllDay: string;
            OpenAllDay: string;
            AddTimeRange: string;
            DeleteTimeRange: string;
            From: string;
            To: string;
            BulkEdit: string;
            ExitBulkEdit: string;
            ApplyToSelected: string;
            SelectDays: string;
            QuickSelectTitle: string;
            Cancel: string;
            Save: string;
            TimeRange: string;
            CurrentHours: string;
            ShowDetailedEdit: string;
            HideDetailedEdit: string;
            QuickSelect: {
              Weekdays: string;
              AllWeek: string;
              Weekend: string;
            };
            Validation: {
              CannotBeBothOpenAndClosed: string;
              InvalidTimeFormat: string;
              OpeningTimeBeforeClosingTime: string;
            };
          };
        };
      };
      OrbitL: {
        Title: string;
        StatusLabel: string;
        RefreshTooltip: string;
        Tariff: {
          Label: string;
          Pos1Cz: string;
          Pos2Cz: string;
          Pos3Cz: string;
          Pos4Cz: string;
        };
        Financing: {
          Label: string;
          Legione: string;
          Hunter: string;
        };
        HunterCapacity: {
          AvailableHelper: string;
          ExceedsError: string;
        };
        Progress: {
          Steps: {
            Completion: string;
            Signing: string;
            Processing: string;
            Dispatch: string;
            DispatchSoftPos: string;
            Done: string;
          };
        };
        Checklist: {
          Heading: string;
          CompletionSuccess: string;
          ToCompleteSection: string;
          GoToSettingsLink: string;
          Items: {
            BasicInfo: string;
            FacilityInfo: string;
            BankAccountInfo: string;
            ExecutiveInfo: string;
            ExecutiveInfoSelfEmployed: string;
            ExecutiveDocuments: string;
            ExecutiveDocumentsSelfEmployed: string;
            TerminalAssignment: string;
          };
          Validation: {
            BasicInfo: string;
            FacilityInfo: string;
            BankAccountInfo: string;
            ExecutiveInfo: string;
            ExecutiveInfoSelfEmployed: string;
            ExecutiveDocuments: string;
            ExecutiveDocumentsSelfEmployed: string;
            TerminalAssignment: string;
          };
          BankReconnect: {
            Explanation: string;
            Button: string;
          };
          MissingFields: {
            MissingExecutive: string;
            PrimaryBankAccount: string;
            BankVerificationAudit: string;
            BirthDate: string;
            Email: string;
            Phone: string;
            PermanentAddress: string;
            PermanentAddressStreet: string;
            PermanentAddressMunicipality: string;
            PermanentAddressPostalCode: string;
            Documents: string;
            FirstDocumentNumber: string;
            FirstDocumentValidity: string;
            FirstDocumentPhotos: string;
            SecondDocumentNumber: string;
            SecondDocumentValidity: string;
            SecondDocumentPhotos: string;
          };
        };
        Rejection: {
          Title: string;
          Intro: string;
          ContractGroup: string;
          DocumentLabel: string;
          DocumentTypes: {
            IdCard: string;
            Passport: string;
            DriverLicense: string;
            OtherDocument: string;
          };
        };
        Phase: {
          RequiresCompletion: string;
          ReadyToDownloadContract: string;
          ContractDownloaded: string;
          SubmittedToComgate: string;
          ProcessingByComgate: string;
          TerminalDispatched: string;
          Completed: string;
        };
        FinancingModel: {
          Hunter: string;
          Legione: string;
        };
        Messages: {
          LoadStatusError: string;
          Processing: string;
          CompletedSuccess: string;
        };
        Contract: {
          DocumentsHeading: string;
          LoanTitle: string;
          LoanFileName: string;
          PaymentServicesTitle: string;
          PaymentServicesFileName: string;
          DownloadedAt: string;
          DownloadButton: string;
          DownloadAgainButton: string;
          AcceptanceLabel: string;
          SubmitButton: string;
          SubmittingButton: string;
          SubmitNote: string;
          DownloadSuccess: string;
          DownloadError: string;
          SubmitSuccess: string;
          SubmitError: string;
          SubmitErrorGeneric: string;
        };
        Handover: {
          DispatchedNoData: string;
          Title: string;
          DispatchedReady: string;
          TableTID: string;
          TableSerialNumber: string;
          TableModel: string;
          Note: string;
          ConfirmButton: string;
          ConfirmingButton: string;
          ConfirmSuccess: string;
          ConfirmError: string;
          ConfirmErrorGeneric: string;
        };
        TerminalSelection: {
          Title: string;
          SelectionCount: string;
          NoTerminalsAvailable: string;
          StockExceeded: string;
          SerialNumber: string;
          HardwareModel: string;
        };
        FacilityTerminals: {
          Title: string;
          TerminalCountLabel: string;
          TerminalCountHelperText: string;
          SoftPosCountLabel: string;
          SoftPosCountHelperText: string;
          SoftPosRequestHelperText: string;
          SoftPosStatus: string;
          TerminalStatus: string;
          NoTerminalsConfigured: string;
          NoAddress: string;
          FullyReserved: string;
          SaveButton: string;
          Saving: string;
          SaveSuccess: string;
          SaveError: string;
          InsufficientStock: string;
          CountsLocked: string;
          LoadError: string;
          AssignedTerminals: string;
        };
        TerminalCountChange: {
          NotificationTitle: string;
          NotificationMessage: string;
          LockedHint: string;
          RequestButton: string;
          ModalTitle: string;
          ModalDescription: string;
          ModalSummaryHeading: string;
          ModalFacilityChange: string;
          ModalTotal: string;
          ModalFacilitySoftPosChange: string;
          ModalSoftPosTotal: string;
          ModalNoteLabel: string;
          ModalNotePlaceholder: string;
          ModalConfirm: string;
          ModalCancel: string;
          PendingTitle: string;
          PendingMessage: string;
          PendingNote: string;
          PendingSoftPosMessage: string;
          RequestSuccess: string;
          RequestError: string;
          RequestConflictError: string;
        };
      };
      TerminalContracts: {
        Title: string;
        LoadingError: string;
        NoTerminalContracts: string;
        AddSuccess: string;
        AddError: string;
        UpdateSuccess: string;
        UpdateError: string;
        DeleteSuccess: string;
        DeleteError: string;
        DeleteConfirmation: string;
        AddButtonText: string;
        NewContractDialogTitle: string;
        EditContractDialogTitle: string;
        ViewContractDialogTitle: string;
        ProductType: { [key in MerchantTerminalContractType]: string };
        Form: {
          Fields: {
            Type: string;
            GprsCount: string;
            TerminalCount: string;
            TerminalCountHelperText: string;
            SoftPosCount: string;
            CountsFromFacilitiesHint: string;
            Currencies: string;
            Tariff: string;
            PayoutIBAN: string;
            PayoutBIC: string;
            PayoutCurrency: string;
            PayoutAccountNumber: string;
          };
        };
        Comgate: {
          SubmitButton: string;
          Submitting: string;
          SubmitConfirmation: string;
          SubmitSuccess: string;
          SubmitError: string;
          SubmitViaOrbitL: string;
          StatusLabel: string;
          Statuses: {
            REQUESTED: string;
            PROCESSING: string;
            WAITING_FOR_DOCUMENTS: string;
            WAITING_FOR_AUTHORIZATION_PAYMENT: string;
            ERRORS_IN_DOCUMENTS: string;
            ERROR_ACTION_PENDING: string;
            REJECTED: string;
            ASSOCIATION_REGISTERING: string;
            READY: string;
          };
        };
      };
      AppProducts: {
        Title: string;
        LoadingError: string;
        NoProducts: string;
        Form: {
          AddError: string;
          AddErrorCountsLocked: string;
          RemoveError: string;
          Prerequisites: {
            Title: string;
            FacilityAdded: string;
            FacilityPhotos: string;
          };
        };
        Products: { [key in ProductCode]: string };
        UnknownProduct: string;
        ProductDescriptions: { [key in ProductCode]: string };
        ProductAccordion: {
          Activate: string;
          Active: string;
          Deactivate: string;
          Preview: string;
          Default: string;
          ProductNotActivated: string;
          SoftPosViaChangeRequest: string;
          SoftPosContactSupport: string;
          FeeWithPricing: string;
          FeeWithCategoryAndPricing: string;
          IndividualPricing: string;
        };
      };
      Pricings: {
        LoadingError: string;
        DirectPaymentError: string;
        PricingSettingError: string;
        PricingChangeError: string;
        SelectPricingLabel: string;

        FeeWithFee: string;
        FeeWithCategoryAndFee: string;
        FeeWithFeeAndAssignedCount: string;
        FeeWithCategoryFeeAndAssignedCount: string;
        CategoryNames: Record<ProductPricingCategory, string>;
      };
    };
    ProfilePhoto: {
      ChangeAction: string;
      RemoveAction: string;
      CropDialogTitle: string;
      ZoomLabel: string;
      CropSave: string;
      CropCancel: string;
      FileTooLarge: string;
      InvalidFileType: string;
    };
    UserAccount: {
      ProfilePhoto: {
        Hint: string;
        RemoveConfirm: string;
        UploadSuccess: string;
        UploadError: string;
        DeleteSuccess: string;
        DeleteError: string;
      };
      ChangePassword: {
        SectionTitle: string;
        OpenAction: string;
        DialogTitle: string;
        OldPasswordLabel: string;
        NewPasswordLabel: string;
        NewPasswordConfirmLabel: string;
        Submit: string;
        Cancel: string;
        Success: string;
        NewMustDiffer: string;
        WrongOldPassword: string;
        GenericError: string;
        RateLimitError: string;
      };
    };
    Onboarding: {
      LogoutButtonLabel: string;
      LicensePaymentReceived: string;
      LicensePaymentProcessing: string;
      SSO: {
        MerchantDescription: string;
        CustomerDescription: string;
        HunterDescription: string;
        Merchant: string;
        Customer: string;
        FirstLogin: string;
      };
    };
    Shared: {
      Filters: {
        Title: string;
        TimePeriod: string;
        TimePeriodLabels: Record<TimePeriod, string>;
        Unlimited: string;
        DateFrom: string;
        DateTo: string;
        DateRange: string;
        PayPoint: string;
        AllPayPoints: string;
        Employee: string;
        AllEmployees: string;
        Status: string;
        AllStatuses: string;
        Apply: string;
        Reset: string;
        RefundOrCancellation: string;
        ShowOnlyRefundsAndCancellations: string;
      };
      HunterList: {
        MainHunterCard: {
          YourCommission: string;
        };
      };
      NotificationTypeSelect: {
        SelectNotificationType: string;
      };
      CodeListAutocomplete: {
        Placeholder: string;
        SelectAll: string;
      };
      HunterAutocomplete: {
        SearchHunter: string;
        SearchHunterMinLength: string;
        SearchHunterNoResults: string;
        SearchHunterPlaceholder: string;
      };
      MerchantUserAutocomplete: {
        Placeholder: string;
        MinLength: string;
        NoResults: string;
      };
      AddressAutocomplete: {
        MinLength: string;
        NoResults: string;
        Error: string;
      };
      AddressInput: {
        SearchLabel: string;
        EnterManually: string;
        SearchInRegistry: string;
        NotInRegistry: string;
        HasErrors: string;
        UncommittedQuery: string;
      };
      Carousel: {
        ScrollLeft: string;
        ScrollRight: string;
      };
      Progress: {
        AriaLabel: string;
      };
      CardsList: {
        View: string;
        Edit: string;
        Delete: string;
        Download: string;
      };
      Dropdown: {
        NoOptions: string;
      };
      PoliticallyExposedDescription: string;
      DownloadPartyIdentificationsButtonLabel: string;
      Payments: {
        Amount: string;
        VariableSymbol: string;
        Note: string;
      };
      SettlementPeriodSelector: {
        Placeholder: string;
        NoSettlementPeriods: string;
      };
      BankingAccountSelector: {
        NoBankAccounts: string;
        ConnectBankAccount: string;
      };
    };
    Settlements: {
      SettlementStatus: Record<SettlementStatus, string>;
      TotalEarnings: string;
      CardEarnings: string;
      TransactionCosts: string;
      SettlementCount: string;
      NoSettlementsFound: string;
      LoadingError: string;
      SettlementControls: {
        Filter: {
          Title: string;
          All: string;
          Paid: string;
          Unpaid: string;
          Overdue: string;
        };
        Sort: {
          Title: string;
          DateDesc: string;
          DateAsc: string;
          AmountDesc: string;
          AmountAsc: string;
        };
      };
      SettlementList: {
        UpdateStatusSuccess: string;
        UpdateStatusError: string;
        DownloadReportError: string;
        DownloadReportFilePrefix: string;
        SettlementPeriod: string;
        SettlementDate: string;
        DueDate: string;
        Amount: string;
        Status: string;
        Actions: string;
        DownloadReport: string;
        MarkAsPaid: string;
        Pay: string;
        SettlementStatusTooltip: string;
        SettlementTypeHunterCommissionFunding: string;
        SettlementTypeDefault: string;
        DownloadInvoice: string;
        DownloadInvoiceError: string;
        InvoiceFilePrefix: string;
        RegenerateInvoice: string;
        RegenerateInvoiceSuccess: string;
        RegenerateInvoiceError: string;
      };
      SettlementDownloadButton: {
        Title: string;
        Downloading: string;
        Download: string;
        RegionPlaceholder: string;
        Regions: {
          CZ: string;
          SK: string;
        };
      };
    };
    Transactions: {
      Tabs: {
        Transfers: string;
        Cards: string;
        AriaLabel: string;
      };
      Title: string;
      LoadMore: string;
      Export: string;
      NoTransactions: string;
      PendingRefunds: string;
      MerchantDetailTransactionFees: {
        Title: string;
      };
      TransactionPaymentOptions: {
        DirectPaymentButton: string;
        ShowPaymentDataButton: string;
      };
      Filters: {
        Direction: string;
        AllDirections: string;
      };
      Summary: {
        Revenue: Record<TimePeriod, string>;
        UnlimitedRevenue: string;
        FilteredRevenue: string;
        FilterSuffix: string;
      };
      Search: {
        Placeholder: string;
      };
      ExportActions: {
        Download: string;
        Email: string;
      };
      ExportDrawer: {
        ExportSelectionPdf: string;
        ExportSelectionXlsx: string;
        ExportSelectionCsv: string;
        ExportTodayRevenueCsv: string;
        SendToEmail: string;
        EmailAddress: string;
        EnterEmailAddress: string;
        Send: string;
        Cancel: string;
        EmailSentSuccess: string;
        EmailSentError: string;
      };
      Direction: {
        Incoming: string;
        Outgoing: string;
        IncomingRefund: string;
        OutgoingRefund: string;
      };
    };
    CardTransactions: {
      Title: string;
      LoadMore: string;
      NoCardTransactions: string;
      Columns: {
        DateTime: string;
        Terminal: string;
        Operator: string;
        Amount: string;
        Status: string;
        CardBrand: string;
        Pan: string;
        Refund: string;
      };
      Status: {
        Approved: string;
        Rejected: string;
        Canceled: string;
        Pending: string;
        Unknown: string;
      };
      Direction: {
        Sale: string;
        Refund: string;
        Cancellation: string;
        Adjustment: string;
      };
      Summary: {
        NetAmount: string;
        Sales: string;
        SalesCount: string;
        Refunds: string;
        RefundsCount: string;
      };
      Export: {
        Download: string;
        Email: string;
        ExportPdf: string;
        ExportXlsx: string;
        ExportCsv: string;
        SendToEmail: string;
        EmailAddress: string;
        EnterEmailAddress: string;
        Send: string;
        Cancel: string;
        EmailSentSuccess: string;
        EmailSentError: string;
        DownloadError: string;
      };
    };
    Sell: {
      OfferValid: string;
    };
    Affiliate: {
      Campaigns: {
        SelectCampaign: string;
        SelectCampaignPlaceholder: string;
        SelectCampaignDescription: string;
        Validity: string;
      };
      AnonymousPayer: string;
      LeadStatus: Record<LeadStatus, string>;
      ThanksToYou: {
        Participation: {
          Title: string;
          OptedInStatus: string;
          OptOutButton: string;
          OptOutDialogTitle: string;
          OptOutDialogIrreversible: string;
          OptOutDialogLoseTitle: string;
          OptOutDialogLoseReferrals: string;
          OptOutDialogLoseInviter: string;
          OptOutDialogKeepTitle: string;
          OptOutDialogKeepRewards: string;
          OptOutDialogKeepAccount: string;
          OptOutDialogEmailNote: string;
          OptOutDialogConfirm: string;
          EmailSent: string;
          ResendButton: string;
          EmailSentToast: string;
          EmailSendError: string;
          EmailCooldownError: string;
        };
        Join: {
          Title: string;
          Text: string;
          Button: string;
          JoinedToast: string;
        };
        Withdrawn: {
          Title: string;
          Text: string;
          PayoutBalance: string;
        };
        WithdrawalPage: {
          Title: string;
          Prompt: string;
          ConfirmButton: string;
          Success: { Title: string; Text: string };
          AlreadyWithdrawn: { Title: string; Text: string };
          Expired: { Title: string; Text: string };
          Error: { Title: string; Text: string };
          ContinueButton: string;
          OpenSectionButton: string;
        };
      };
      AffiliateStats: {
        Invited: string;
        Registered: string;
        WithActiveLicense: string;
        AnonymousPayment: string;
      };
      AffiliateLink: {
        Title: string;
        LoadingError: string;
        CopyLink: string;
        LinkCopied: string;
        ShareButton: {
          Title: string;
          Text: string;
        };
      };
      AffiliateLinkWithActions: {
        InviteFriend: string;
        ShareButton: string;
        EmailButton: string;
        QrButton: string;
        QrCodeFileName: string;
        QrInstructions: string;
        LinkTypeToggle: {
          Title: string;
          Registration: string;
          MooneInfo: string;
        };
      };
      CodeField: {
        SectionTitle: string;
        Label: string;
        Placeholder: string;
        Verified: string;
        RecommendedBy: string;
        Locked: string;
        LockedBy: string;
        ApplyFailed: string;
        Error: {
          AffiliateCodeNotFound: string;
          AffiliateCodeOwn: string;
          AffiliateCodeNotApplicable: string;
          AffiliateReferrerAlreadySet: string;
          AffiliateReferrerTooLate: string;
          TooManyRequests: string;
          Unavailable: string;
          Unverified: string;
        };
      };
      LeadInvitation: {
        NewInvitation: string;
        FriendInvited: string;
        UserAlreadyExists: string;
        InvitationAlreadySent: string;
        GeneralError: string;
        SendInvitation: Record<AffiliateUserRole, string>;
      };
      UserAffiliate: {
        MooneLiteWarning: string;
        Title: string;
        Subtitle: string;
        Description: string;
        HowItWorksTitle: string;
        HowItWorks: string;
        Rewards: string;
        TermsLinkPretext: string;
        YourAffiliateLink: string;
        TermsLinkUrl: string;
        TermsLink: string;
        Tabs: {
          Customer: string;
          Campaigns: string;
        };
      };
      HunterAffiliate: {
        Title: string;
        Description: string;
        InviteNow: string;
        TermsLinkPretext: string;
        YourAffiliateLink: string;
        TermsLinkUrl: string;
        TermsLink: string;
        Tabs: {
          Hunter: string;
          Customer: string;
          Merchant: string;
          Campaigns: string;
        };
        MerchantLicenseSelector: {
          Label: string;
        };
        InviteExistingUser: {
          Title: string;
          Description: string;
          ButtonLabel: string;
        };
      };
      MerchantAffiliate: {
        Title: string;
        Description: string;
        InviteNow: string;
        TermsLinkPretext: string;
        YourAffiliateLink: string;
        TermsLinkUrl: string;
        TermsLink: string;
        Tabs: {
          Customer: string;
          Campaigns: string;
        };
      };
      Earnings: {
        Title: string;
        TotalRegisteredUsers: string;
        TierBreakdown: string;
        CurrentPayout: string;
        TotalEarned: string;
        RegisteredUsersHint: string;
        PayoutHint: string;
        TotalEarnedHint: string;
        CombinedPayoutHint: string;
        CombinedTotalEarnedHint: string;
        CombinedValorPayoutHint: string;
        CombinedValorTotalEarnedHint: string;
      };
    };
    Coinback: {
      Commission: string;
    };
  };
  Hooks: {
    BankingAuth: {
      Error: string;
    };
    OCR: {
      Error: string;
      Declined: string;
    };
    Paypoint: {
      PaypointEdited: string;
      PaypointCreated: string;
      PaypointEditError: string;
      PaypointCreateError: string;
      AssignNfcCardNoConflict: string;
      AssignNfcCardSuccess: string;
      AssignNfcCardError: string;
      ScanError: string;
    };
    Transaction: {
      TipUpdateError: string;
    };
    BankingAccount: {
      Deleted: string;
      DeleteError: string;
      PrimaryAccountChanged: string;
      PrimaryAccountChangeError: string;
      StatusChanged: string;
      StatusChangeError: string;
    };
    TerminalContract: {
      DownloadError: string;
      UcbContractPrefix: string;
    };
    Verifications: {
      Error: string;
    };
    VerificationEmail: {
      Error: string;
      Success: string;
    };
    CampaignTransaction: {
      Error: string;
      Success: string;
    };
    Ares: {
      Error: string;
      SubjectNotFound: string;
    };
    Employee: {
      AccessSent: string;
      BackOfficeTargetError: string;
      AccessSendError: string;
      EmployeeExistsError: string;
      InviteeNotRegisteredError: string;
      InvitationCancelled: string;
      InvitationCancelError: string;
      EmployeeRemoved: string;
      EmployeeRemoveError: string;
    };
    Notifications: {
      Created: string;
      CreateError: string;
      Updated: string;
      UpdateError: string;
      Deleted: string;
      DeleteError: string;
      Restored: string;
      RestoreError: string;
    };
  };
  Roles: {
    AddRole: {
      Banner: {
        Hunter: string;
        Merchant: string;
        SignUpIntent: {
          Hunter: string;
          Merchant: string;
        };
        Button: string;
      };
      Completion: {
        Submit: string;
        Success: string;
        Error: string;
      };
      PickerTitle: string;
      MigrationRequired: string;
      KycRequired: string;
      BankVerificationRequired: string;
      BackOfficeAccount: string;
      HunterCard: {
        Title: string;
        Description: string;
        AlreadyHeld: string;
      };
      MerchantCard: {
        Title: string;
        Description: string;
      };
      HunterForm: {
        Description: string;
      };
      MerchantForm: {
        Description: string;
      };
      BankStep: {
        Connected: string;
      };
      Form: {
        CompanyNumber: string;
        Region: string;
        Submit: string;
        Success: string;
        Error: string;
      };
    };
  };
  Social: {
    Handle: {
      Label: string;
      Placeholder: string;
      ValidationError: string;
      Reserved: string;
      Available: string;
      Taken: string;
      SaveError: string;
      StepTitle: string;
      StepDescription: string;
      StepBenefitSearch: string;
      StepBenefitSearchHandleFallback: string;
      StepBenefitSearchUrlFallback: string;
      StepBenefitFollows: string;
      StepBenefitChat: string;
      StepBenefitLeaderboard: string;
      StepMissingWarning: string;
      ContinueWithoutHandle: string;
      ChangeNote: string;
      MissingBannerText: string;
      MissingBannerAction: string;
    };
    Terms: {
      /** Trans: <terms>, <chatTerms>, <communityRules> wrap the document links. */
      CheckboxLabel: string;
      /** Trans: <privacy> links the privacy policy, <summary> expands the inline summary. */
      Note: string;
      ShowSummary: string;
      HideSummary: string;
      AcceptAndContinue: string;
      Required: string;
      /**
       * Documents of the Chat contract with Mo.one Digital Services s.r.o. Every URL is per role
       * family (Customer / Merchant / Hunter) — some roles share one PDF today, but each may get
       * its own version. Terms, ChatTerms and CommunityRules are the three the consent checkbox
       * names; the rest only appear in the "Shrnutí a dokumenty" download list.
       */
      Documents: {
        ContractSummary: SocialTermsDocument;
        Terms: SocialTermsDocument;
        ChatTerms: SocialTermsDocument;
        CommunityRules: SocialTermsDocument;
        ComplaintRules: SocialTermsDocument;
        TechnicalTerms: SocialTermsDocument;
        PrivacyPolicy: SocialTermsDocument;
        /** Label above the download list. */
        ListLabel: string;
      };
      /** "Ukončit Chat" section of the user settings (termination = clearing the handle). */
      Terminate: {
        Title: string;
        Description: string;
        Action: string;
        ConfirmTitle: string;
        ConfirmText: string;
        Success: string;
      };
      /** Contract summary rows of the "Shrnutí a dokumenty" block. */
      Summary: Record<
        | "Service"
        | "Provider"
        | "Price"
        | "Duration"
        | "Termination"
        | "Withdrawal"
        | "Payments"
        | "Complaints"
        | "Devices"
        | "Important",
        { Label: string; Value: string }
      >;
    };
    PublicProfile: {
      PrivateAccount: string;
      NotFound: string;
      Follow: string;
      Following: string;
      FollowBack: string;
      Requested: string;
      Mutual: string;
      ApproveRequest: string;
      DeleteRequest: string;
      MutualConfirmTitle: string;
      MutualConfirmInbox: string;
      MutualConfirmName: string;
      SendMessage: string;
      SendMessageError: string;
      SendMessagePhoneRequired: string;
      MoreActions: string;
      Block: string;
      BlockConfirmTitle: string;
      BlockConfirmDescription: string;
      Report: string;
    };
    People: {
      SearchPlaceholder: string;
      SearchAllUsersPlaceholder: string;
      SearchNoResults: string;
      RequestsTitle: string;
      OutgoingTitle: string;
      Approve: string;
      Delete: string;
      CancelRequest: string;
      MutualTitle: string;
      NoMutuals: string;
      FollowingTitle: string;
      NoFollowing: string;
      FollowersTitle: string;
      NoFollowers: string;
      Unfollow: string;
      BlockedTitle: string;
      NoBlocked: string;
      Unblock: string;
      Tabs: {
        Mutual: string;
        Following: string;
        Followers: string;
      };
      HandleGateTitle: string;
      HandleGateText: string;
      HandleGateAction: string;
    };
    PublicPermissions: {
      IdentityLabel: string;
      IdentityVerifiedTooltip: string;
      IdentityVerificationPendingTooltip: string;
      PublicProfileLabel: string;
      PhoneVerifiedTooltip: string;
      PhoneVerificationPendingTooltip: string;
      PhoneUnverifiedTooltip: string;
      VerifyAction: string;
      BioLabel: string;
      BioTooLong: string;
      BioSaveError: string;
      SectionDescription: string;
      GoPublicConfirmTitle: string;
      GoPublicConfirmRequests: string;
      GoPublicConfirmMutual: string;
      GoPublicConfirmAction: string;
      GoPublicSummaryUnavailable: string;
    };
    Stats: {
      Mutual: string;
      Followers: string;
      Following: string;
    };
  };
  Pages: {
    Titles: {
      AddRole: string;
      Valor: string;
      Stardust: string;
      People: string;
      Followers: string;
      BlockedUsers: string;
      Profile: string;
      Notifications: string;
      Invitations: string;
      ThanksToYou: string;
      IncomingTransaction: string;
      OutgoingTransaction: string;
      UnknownTransaction: string;
      Licenses: string;
      NewPayment: string;
      MerchantOverview: string;
      Employees: string;
      PayPoints: string;
      BankAccountDetail: string;
      SetPassword: string;
      BankAccounts: string;
      ToBeVerified: string;
      BankAccountOrder: string;
      LanguageSettings: string;
      Merchants: string;
      Welcome: string;
      User: string;
      Withdrawal: string;
      Settlements: string;
      MerchantOpenRegistrations: string;
      LicenseOrder: string;
      Onboarding: string;
      CompleteSSORegistration: string;
      CompleteCustomerOnboarding: string;
      LeadInvitation: string;
      MerchantInitiatePayment: string;
      MerchantAcceptPayment: string;
      MerchantCompleteRegistration: string;
      MerchantNewRegistration: string;
      LegalDocuments: string;
      Redeem: string;
      AnonymousDashboard: string;
      BankingAuthQr: string;
      SystemReport: string;
      SystemControl: string;
      BankingAuth: string;
      UserAccount: string;
      Help: string;
      ColdWallet: string;
      MerchantSettlements: string;
      AnonymousTransactionDetail: string;
      MerchantFeeCalculator: string;
      RedeemPublic: string;
      ApplicationError: string;
      GoogleCallback: string;
      AwaitingEmailVerification: string;
      BankIDRedirect: string;
      Error403: string;
      DeactivateUser: string;
      CustomerAnonymousPaymentPay: string;
      TransactionsHistory: string;
      TransactionDetail: string;
      Users: string;
      Settlement: string;
      FeeCalculator: string;
      Contact: string;
      Dashboard: string;
      Settings: string;
      AmlUsers: string;
      NotificationsManagement: string;
      NewNotification: string;
      EditNotification: string;
      WhereToPay: string;
      RolesPermissions: string;
      AllMerchants: string;
      UnassignedMerchants: string;
      Hunters: string;
      HunterPortal: string;
      Transfer: string;
      BatchPayments: string;
      BatchPaymentDetail: string;
      Campaigns: string;
      Coupons: string;
      Terminals: string;
      Raffle: string;
      SsoClients: string;
      SsoAuthorize: string;
      ConnectedApps: string;
      AdminCampaignCreate: string;
      AdminCampaignEdit: string;
      Chat: string;
      Devices: string;
      PaymentGateways: string;
    };
    Content: {
      WhereToPay: {
        Tabs: {
          Stores: string;
          EShops: string;
          Campaigns: string;
        };
        StoresSection: {
          CategoryLabel: string;
          CategoryPlaceholder: string;
          CategoryAccessibility: string;
          MunicipalityLabel: string;
          MunicipalityPlaceholder: string;
          MunicipalityAccessibility: string;
          NoStoresFound: string;
        };
        Campaigns: {
          Title: string;
          ShareLinkText: string;
          CopyLink: string;
          LinkCopied: string;
          Description: string;
        };
        StoreDetail: {
          NotFound: string;
          AboutUs: {
            Title: string;
            Address: string;
            Phone: string;
            Email: string;
          };
          OpeningHours: {
            Title: string;
          };
        };
      };
      SetPassword: {
        CreateNewPassword: string;
        ResetPassword: string;
        Password: string;
        PasswordConfirmation: string;
        SetPassword: string;
      };
      LicenseDetail: {
        LoadingError: string;
        Type: string;
        LicenseNo: string;
        PaymentStatus: string;
        WalletAddress: string;
        Country: string;
        PaymentPendingInfo: string;
        CancelOrder: string;
        CancelOrderConfirmation: string;
        OrderLicense: string;
        AwaitingPayment: string;
        LicenseStatus: { [key in LicensePaymentStatus]: string };
      };
      LegalDocuments: {
        previousVersions: string;
        Documents: {
          mooneLiteTerms: {
            title: string;
            url: string;
          };
          paymentServicesContract: {
            title: string;
            url: string;
          };
          generalBusinessTerms: {
            title: string;
            url: string;
          };
          previousGeneralBusinessTerms: {
            title: string;
            url: string;
          };
          merchantTerms: {
            title: string;
            url: string;
          };
          aisPisProductTerms: {
            title: string;
            url: string;
          };
          settlementManual: {
            title: string;
            url: string;
          };
          priceList: Record<
            ProductPricingCategory,
            {
              title: string;
              url: string;
            }
          >;
          complaintRules: {
            title: string;
            url: string;
          };
          dataProtectionMemorandum: {
            title: string;
            url: string;
          };
          previousDataProtectionMemorandum: {
            title: string;
            url: string;
          };
          previousDataProtectionMemorandum2: {
            title: string;
            url: string;
          };
          precontractInformation: {
            title: string;
            url: string;
          };
          thanksToYou: {
            title: string;
            url: string;
          };
          stardust: {
            title: string;
            url: string;
          };
          stardustCurrentTerms: {
            title: string;
            url: string;
          };
          Hunter: {
            digitalFranchiseContract: {
              title: string;
              url: string;
            };
            affiliateTerms: {
              title: string;
              url: string;
            };
            commissionTable: {
              title: string;
              url: string;
            };
            valorVigorPointRules: {
              title: string;
              url: string;
            };
            valorGeneralRules: {
              title: string;
              url: string;
            };
            nftCollectionRules: {
              title: string;
              url: string;
            };
            generalFranchiseTerms: {
              title: string;
              url: string;
            };
            dataProtectionPrinciples: {
              title: string;
              url: string;
            };
            satisfactionGuarantee: {
              title: string;
              url: string;
            };
            previousDataProtectionPrinciples: {
              title: string;
              url: string;
            };
            previousDataProtectionPrinciples2: {
              title: string;
              url: string;
            };
            previousAffiliateTerms: {
              title: string;
              url: string;
            };
            previousAffiliateTerms2: {
              title: string;
              url: string;
            };
            previousGeneralFranchiseTerms: {
              title: string;
              url: string;
            };
            previousNftCollectionRules: {
              title: string;
              url: string;
            };
            previousCommissionTable: {
              title: string;
              url: string;
            };
          };
        };
      };
      Loyalty: {
        Tabs: {
          Challenges: string;
          Leaderboard: string;
          Rewards: string;
        };
        Challenges: {
          FeaturedChallenges: {
            Title: string;
            AllChallenges: string;
          };
          WeeklyChallenges: {
            Title: string;
            TimeRemaining: string;
          };
          MonthlyChallenges: {
            Title: string;
            TimeRemaining: string;
          };
          DailyChallenges: {
            Title: string;
          };
          LongTermChallenges: {
            Title: string;
          };
          UnlimitedChallenges: {
            Title: string;
          };
          AllChallenges: {
            Title: string;
          };
        };
        Rewards: {
          Title: string;
          Columns: {
            Date: string;
            Activity: string;
          };
        };
        Notification: {
          Completed: string;
        };
        Events: Record<
          LoyaltyEventCode,
          { title: string; description: string }
        >;
      };
      Stardust: {
        PointsLabel: WithPlurals<{ Points: string }>;
        Participation: {
          Title: string;
          OptedInStatus: string;
          OptOutButton: string;
          OptOutDialogTitle: string;
          OptOutDialogText: string;
          OptOutDialogConfirm: string;
          OptedOutToast: string;
        };
        Consent: {
          Title: string;
          Description: string;
          TermsLink: string;
          Confirm: string;
        };
        PilotDisclaimer: string;
        Overview: {
          Points: { title: string; explanationHint: string };
          Payout: { title: string; explanationHint: string };
          Rewards: { title: string; explanationHint: string };
          MyShare: { title: string; explanationHint: string };
          Rules: {
            Title: string;
            Link: string;
          };
          CurrentTerms: {
            Title: string;
            Link: string;
          };
        };
        Leaderboard: {
          Title: string;
        };
        Rewards: {
          Columns: {
            PointsLabel: string;
          };
        };
      };
      Valor: {
        PointsLabel: WithPlurals<{ Points: string }>;
        Overview: {
          Points: { title: string; explanationHint: string };
          Payout: { title: string; explanationHint: string };
          Rewards: { title: string; explanationHint: string };
          MyShare: { title: string; explanationHint: string };
          Rules: {
            Title: string;
            Link: string;
          };
          CurrentChallenges: {
            Title: string;
            Link: string;
          };
        };
        TeslaBanner: {
          Title: string;
          Description: string;
        };
        Leaderboard: {
          Title: string;
        };
        Rewards: {
          Columns: {
            PointsLabel: string;
          };
        };
      };
      Contact: {
        HunterDiscord: {
          Description: string;
          Link: string;
        };
        Phone: string;
        Email: string;
        PhoneDescription: string;
        CompanyInfo: {
          title: string;
          description: string;
          CompanyNumber: string;
          TaxNumber: string;
          CompanyAddress: string;
        };
        HunterCompanyInfo: {
          Title: string;
          CompanyName: string;
          CompanyInfoLine1: string;
          CompanyInfoLine2: string;
          CompanyInfoLine3: string;
        };
        BankInfo: {
          Title: string;
          CZKAccount: Record<AccessRoleCode, string>;
          EURAccount: Record<AccessRoleCode, string>;
        };
      };
      BankAccounts: {
        ConnectedBankAccounts: string;
        CurrencyGroupTitle: string;
        NoBankAccounts: string;
        Error: string;
        PrimaryAccount: string;
        PrimaryAccountDescription: string;
        HunterPrimaryAccountDescription: string;
        MissingLicenseHolderBankAccount: string;
        VerificationStatusesTitle: string;
        QuickPayment: {
          Label: string;
          Description: string;
        };
        EditAccountOrderDescription: string;
        BankIdentityVerificationAccount: string;
        InvalidTokenChip: string;
        EditOrder: string;
        NoAccountSet: string;
        AccountMenu: {
          Renew: string;
          RenewPayments: string;
          Remove: string;
        };
        DeleteAccount: {
          Title: string;
          Prompt: string;
          ReplacementPrimaryAccount: string;
          PrimaryAccountWarning: string;
        };
        ManualBankAccountVerificationPrompt: {
          ManualVerificationNecessary: string;
          ActivateConfirmationPrompt: string;
          RejectConfirmationPrompt: string;
          Activate: string;
          Reject: string;
        };
        ConnectedBankAccount: {
          Owner: string;
          DateCreated: string;
          Status: string;
          BankVerificationSource: string;
        };
        DeletedVerificationAccount: {
          Error: string;
        };
        Detail: {
          IBAN: string;
          AccountOwners: string;
          DateConnected: string;
          AccountNotFound: string;
          BackToList: string;
          IsPrimaryNote: string;
          Actions: {
            SetAsPrimary: string;
            PrimaryAccountDescription: string;
            AuthorizeAccess: string;
            AuthorizePayments: string;
            AuthorizationValidUntil: string;
          };
          IbanCopySuccess: string;
          Alerts: {
            Rejected: { Title: string; Description: string };
            Inactive: { Title: string; Description: string };
            Restricted: { Title: string; Description: string; Action: string };
            AwaitingVerification: { Title: string; Description: string };
            PreApproved: { Title: string; Description: string };
            TokenInvalidAis: {
              Title: string;
              Description: string;
              Action: string;
            };
            TokenExpiringAis: {
              Title: string;
              Description: string;
              Action: string;
            };
            TokenInvalidPisp: {
              Title: string;
              Description: string;
              Action: string;
            };
            TokenExpiringPisp: {
              Title: string;
              Description: string;
              Action: string;
            };
          };
        };
      };
      Help: {
        HelpInPreparation: string;
        ContactEmail: string;
      };
      UserDetailPageContent: {
        SaveSuccess: string;
        SaveError: string;
        DuplicateLoginError: string;
        AddressFields: {
          Legend: string;
          Type: string;
          Street: string;
          StreetNumber: string;
          OrientationNumber: string;
          PostalCode: string;
          Municipality: string;
          Remove: {
            Button: string;
            ConfirmTitle: string;
            ConfirmContent: string;
            SuccessMessage: string;
            ErrorMessage: string;
          };
        };
        Tabs: {
          User: {
            Title: string;
            Form: {
              Login: string;
              Status: string;
              VerificationAdditionalInfoStatus: string;
              VerificationIdentityVerificationStatus: string;
              VerificationRiskProfile: string;
              VerificationBankVerificationStatus: string;
              BankVerificationSource: string;
              BankAccountNumber: string;
              VerificationDailyBuyLimit: string;
              VerificationMonthlyBuyLimit: string;
              Stardust: {
                Legend: string;
                NotAsked: string;
                OptedIn: string;
                OptedOut: string;
                Withdrawn: string;
                ChangedAt: string;
                Source: {
                  Registration: string;
                  ConsentDialog: string;
                  ProgramSection: string;
                };
              };
              ThanksToYou: {
                Legend: string;
                NotAsked: string;
                OptedIn: string;
                OptedOut: string;
                Withdrawn: string;
                ChangedAt: string;
                Source: {
                  Registration: string;
                  ConsentDialog: string;
                  ProgramSection: string;
                };
              };
              Legend: {
                Account: string;
                Verification: string;
                Bank: string;
                Limits: string;
              };
            };
          };
          Party: {
            Title: string;
            SetVerified: string;
            CancelVerification: string;
            NoDocumentsUploaded: string;
            NoActingPersonLinked: string;
            IdentityVerificationLegend: string;
            IdentityVerified: string;
            IdentityNotVerified: string;
            Form: {
              SubmitSuccess: string;
              SubmitError: string;
              Save: string;
              ClientIdentifier: string;
              CopyClientIdentifier: string;
              CopyClientIdentifierSuccess: string;
              NaturalPerson: {
                Type: string;
                FirstName: string;
                LastName: string;
                PersonalNumber: string;
                BirthDate: string;
                Gender: string;
                BirthPlace: string;
                Nationality: string;
                Phone: string;
                VerifiedPhone: string;
                Email: string;
                Legend: {
                  PersonalData: string;
                  Contact: string;
                  Documents: string;
                };
              };
              PartyIdentification: {
                Identification: string;
                IdentificationNumber: string;
                IdentificationValidFrom: string;
                IdentificationValidTo: string;
                IdentificationIssuer: string;
                NoIdentificationsUploaded: string;
                IdentificationsDownloadPrefix: string;
                SecondDocumentTitle: string;
                AutomatedCheck: {
                  Title: string;
                  NoChecks: string;
                  LoadingError: string;
                  Document: string;
                  Documents: {
                    Primary: string;
                    Secondary: string;
                  };
                  Verdict: string;
                  Date: string;
                  DocumentType: string;
                  RequestID: string;
                  Warnings: string;
                  Error: string;
                };
              };
              LegalEntity: {
                Type: string;
                CompanyName: string;
                CompanyNumber: string;
                TaxNumber: string;
                Phone: string;
                Email: string;
                Legend: {
                  Company: string;
                  Contact: string;
                };
                RegistryLinks: {
                  Or: string;
                  Isir: string;
                  Esm: string;
                };
              };
              SelfEmployed: {
                Legend: {
                  Company: string;
                  Contact: string;
                  Documents: string;
                };
                Type: string;
                CompanyName: string;
                FirstName: string;
                LastName: string;
                CompanyNumber: string;
                TaxNumber: string;
                PersonalNumber: string;
                BirthDate: string;
                Gender: string;
                BirthPlace: string;
                Nationality: string;
                Phone: string;
                VerifiedPhone: string;
                Email: string;
              };
            };
          };
          BankAccounts: {
            Title: string;
            Subtitle: string;
          };
          Licenses: {
            Title: string;
            Empty: string;
            Transfer: {
              Button: string;
              Title: string;
              Description: string;
              TargetLabel: string;
              Submit: string;
              Success: string;
              Error: string;
            };
            Form: {
              Controls: {
                SubmitSuccess: string;
                SubmitError: string;
                Save: string;
                SendInvoice: string;
                SendingInvoice: string;
                SendInvoiceSuccess: string;
                SendInvoiceError: string;
                InvoiceSent: string;
                RegenerateInvoice: string;
                RegeneratingInvoice: string;
                RegenerateInvoiceSuccess: string;
                RegenerateInvoiceError: string;
              };
              Fields: {
                Code: string;
                LicenseNo: string;
                PaymentDate: string;
                PaymentStatus: {
                  label: string;
                  options: {
                    Initiated: string;
                    Processing: string;
                    Success: string;
                    Fail: string;
                    Cancelled: string;
                  };
                };
                WalletAddress: string;
              };
            };
          };
          Verification: {
            Title: string;
            Form: {
              Controls: {
                SubmitSuccess: string;
                SubmitError: string;
                Save: string;
              };
              Fields: {
                TaxResidencyCountryID: string;
                BusinessActivity: string;
                OtherBusinessActivityDetails: string;
                SourceOfIncome: string;
                OtherSourceOfIncomeDetails: string;
                LegacySourceOfIncome: string;
                ExpectedMonthlyRevenue: string;
                AverageTransactionValue: string;
                IsPoliticallyExposedPerson: string;
                HasNonEUResidence: string;
                HasNonEUNationality: string;
              };
            };
          };
          Settlement: {
            Title: string;
          };
          HunterPrerequisites: {
            Title: string;
            SelectAll: string;
            DeselectAll: string;
            LoadingError: string;
            SaveSuccess: string;
            SaveError: string;
            Note: string;
            NotePlaceholder: string;
            AutoVerified: string;
            Items: {
              RegisterAsMerchant: string;
              InviteUserViaAffiliate: string;
              BanksAndPayments: string;
              OfflineTraining: string;
              BringMerchantsWithHunter: string;
            };
          };
          RelatedParties: {
            Title: string;
            NoRelatedParties: string;
            Add: {
              Button: string;
              Title: string;
              RelationTypeLabel: string;
              BeneficialOwnerOption: string;
              Submit: string;
              AddSuccess: string;
              AddError: string;
              LinkError: string;
              DocumentsTitle: string;
              DocumentsIncomplete: string;
            };
            Delete: {
              Button: string;
              ConfirmTitle: string;
              ConfirmContent: string;
              SuccessMessage: string;
              ErrorMessage: string;
            };
            Form: {
              Controls: {
                SubmitSuccess: string;
                SubmitError: string;
                LoadingError: string;
                Save: string;
              };
              Fields: {
                RelationType: string;
                IsOwner: string;
                BeneficialOwnerFields: {
                  Legend: {
                    PersonalData: string;
                  };
                  FirstName: string;
                  LastName: string;
                  BirthDate: string;
                  Nationality: string;
                };
                RelatedPartyFields: {
                  Legend: {
                    PersonalData: string;
                    Contact: string;
                    Documents: string;
                    FirstDocument: string;
                  };
                  DocumentsExpiringSoon: string;
                  DocumentsExpired: string;
                  DocumentsValidityMissing: string;
                  FirstName: string;
                  LastName: string;
                  PersonalNumber: string;
                  BirthDate: string;
                  Gender: string;
                  BirthPlace: string;
                  Nationality: string;
                  Phone: string;
                  Email: string;
                };
              };
            };
          };
        };
      };
      MerchantFeeCalculator: {
        CardRatesTable: {
          Expand: string;
          Collapse: string;
          Headers: {
            CardType: string;
            Interchange: string;
            SchemeFee: string;
            CurrentTotalRate: string;
            ProposedTotalRate: string;
          };
        };
        CurrentTotalFee: string;
        ProposedTotalFee: string;
        HideFeeTable: string;
        ShowFeeTable: string;
        MonthlyTurnover: string;
        AverageTransactionValue: string;
        MooneDirectPayShare: string;
        Currency: string;
        FeeSettings: {
          CurrentFees: string;
          ProposedFee: string;
          DirectPaymentWithMoone: string;
          ProviderFee: string;
          Acquiring: string;
          FixedTransactionCost: string;
          MooneFee: string;
        };
        CardTypeRatioSlider: {
          Title: string;
          ToggleButtons: {
            MostlyPersonal: string;
            SomeCommercial: string;
            MostlyCommercial: string;
          };
          CardTypeRatios: {
            Personal: string;
            Commercial: string;
          };
        };
        YearlySavingsWidget: {
          PotentialYearlySavings: string;
          WithLegione: string;
          WithMoone: string;
        };
        CostsChart: {
          Title: string;
          HighContrast: string;
          AnnualTransactionCosts: string;
          TickLabels: {
            CurrentCosts: {
              Mobile: string;
              Full: string;
              Description: string;
            };
            LegioneCosts: {
              Mobile: string;
              Full: string;
              Description: string;
            };
            MooneCosts: {
              Mobile: string;
              Full: string;
              Description: string;
            };
          };
        };
      };
      Dashboard: {
        BankAccountsWidget: {
          Title: string;
          ConnectionFailed: string;
          NoActiveAccounts: string;
        };
        PreApprovalWidget: {
          Title: string;
          Description: string;
          LimitPerTransaction: string;
          LimitTotal: string;
          Remaining: string;
        };
        TransactionFees: {
          Admin: Record<FeesPeriodVariant, string>;
        };
      };
      Users: {
        ExcludeAnonymous: string;
        Error: string;
        VerificationCell: {
          AdditionalInfo: string;
          IdentityVerification: string;
          RiskProfile: string;
          BankVerification: string;
        };
        TableHeaders: {
          Verification: string;
          Login: string;
          Role: string;
          Status: string;
          FirstName: string;
          LastName: string;
          Company: string;
          ClientIdentifier: string;
          DateCreated: string;
          PaymentReference: string;
          Version: string;
          DocumentExpiration: string;
          Settlement: string;
          Admin: string;
        };
        Filters: {
          ExpiringWithin30Days: string;
          Expired: string;
        };
        ConnectedBankAccounts: {
          LoadingError: string;
          NoBankAccounts: string;
          UnassignedContext: string;
        };
        ToBeVerified: {
          Subtitles: {
            UserVerification: string;
            BankVerification: string;
          };
          AdditionalInfo: string;
          RiskProfile: string;
          BankVerification: string;
          IdentityVerification: string;
        };
        RolesPermissions: {
          TableHeaders: {
            RoleName: string;
            RoleCode: string;
            AccessRightCode: string;
            CanRead: string;
            CanCreate: string;
            CanUpdate: string;
            CanDelete: string;
            CanExport: string;
          };
          NoAccessRights: string;
          AccessRightSingular: string;
          AccessRightPlural: string;
        };
      };
      AmlUsers: {
        Error: string;
        ExportButton: string;
        ExportUnderConstruction: string;
        TableHeaders: {
          Verification: string;
          ClientIdentifier: string;
          Company: string;
          PersonalNumber: string;
          Role: string;
          PartyType: string;
          FirstName: string;
          LastName: string;
          RiskProfile: string;
          DateCreated: string;
        };
        Detail: {
          LoadingError: string;
          ConnectedBankAccounts: string;
          RiskProfileSettings: string;
          RiskProfileUpdateError: string;
          RiskProfileUpdateSuccess: string;
          ClientIdentifier: string;
          CopyClientIdentifier: string;
          CopyClientIdentifierSuccess: string;
        };
      };
      Merchants: {
        Error: string;
        StatusWidgets: {
          OpenRegistrations: string;
          InvitationsSent: string;
          InOnboarding: string;
          Completed: string;
        };
        TableHeaders: {
          Status: string;
          CompanyName: string;
          CompanyNumber: string;
          LicenseType: string;
          LicenseOwner: string;
          LicenseNumber: string;
          DateCreated: string;
          TransactionsCount: string;
        };
        OrbitLOnboarding: {
          Title: string;
          ActionRequiredBadge: string;
          LoadingError: string;
          Table: {
            CompanyNumber: string;
            CompanyName: string;
            Hunter: string;
            ActivatedAt: string;
            Phase: string;
            ComgateStatus: string;
            ComgateMID: string;
            TimeSinceSubmit: string;
            LastStatusChange: string;
            FinancingModel: string;
            Hours: string;
            Days: string;
            TerminalCount: string;
            TerminalCountRequest: string;
            OnlyOpenRequests: string;
          };
          TerminalCountChange: {
            SectionTitle: string;
            OpenRequestHeading: string;
            RequestedBy: string;
            SystemActor: string;
            ContractTotal: string;
            ContractSoftPosTotal: string;
            ContractLevelChange: string;
            SoftPosChange: string;
            SoftPosTotal: string;
            HistoryHeading: string;
            NoHistory: string;
            ResolveButton: string;
            DismissButton: string;
            ResolveHint: string;
            DismissDialogTitle: string;
            DismissNoteLabel: string;
            DismissNoteRequired: string;
            DismissConfirm: string;
            Cancel: string;
            ResolveSuccess: string;
            DismissSuccess: string;
            Error: string;
            Source: { [key in TerminalCountChangeSource]: string };
            Status: { [key in TerminalCountChangeStatus]: string };
          };
          Detail: {
            Title: string;
            CloseButton: string;
            ChecklistSection: string;
            RejectionSection: string;
            TerminalsSection: string;
            CommentsSection: string;
            NoTerminals: string;
            NoComments: string;
            SendNoteLabel: string;
            SendNoteButton: string;
            SendNotePlaceholder: string;
            SendNoteSuccess: string;
            SendNoteError: string;
            ResubmitButton: string;
            ResubmitConfirmTitle: string;
            ResubmitConfirmText: string;
            ResubmitConfirmButton: string;
            ResubmitCancelButton: string;
            ResubmitSuccess: string;
            ResubmitError: string;
            CommentAuthor: string;
            CommentDate: string;
            SwapTerminal: string;
            SwapTerminalTitle: string;
            SwapTerminalSearch: string;
            SwapTerminalConfirm: string;
            SwapTerminalCancel: string;
            SwapTerminalSuccess: string;
            SwapTerminalSuccessWithCode: string;
            SwapTerminalError: string;
            SwapTerminalNoResults: string;
            SwapTerminalTmsUpdateWarning: string;
            TerminalStatusDeployed: string;
            Tid: string;
            TidTypesSection: string;
            TidTypesHint: string;
            TidTypePhysical: string;
            TidTypeSoftPos: string;
            TidTypeUnknown: string;
            TidTypeManual: string;
            SaveTidTypes: string;
            SaveTidTypesSuccess: string;
            SaveTidTypesError: string;
            SaveTidTypesExceedError: string;
            RegisterTmsButton: string;
            RegisterTmsSuccess: string;
            RegisterTmsError: string;
            ActivationCode: string;
            ReactivateCodeButton: string;
            ReactivateCodeSuccess: string;
            ReactivateCodeError: string;
          };
          FindByTerminal: {
            Button: string;
            Title: string;
            SearchLabel: string;
            NoResults: string;
            Close: string;
          };
        };
      };
      Settlements: {
        Error: string;
        SettlementTrigger: string;
        SettlementTriggerError: string;
        SettlementTriggerSuccess: string;
        SettlementTriggerLoading: string;
        SettlementTriggerButtonText: string;
      };
      Welcome: {
        RegistrationSuccess: string;
        UnverifiedAccountLimits: string;
        TransactionLimitsLink: string;
        Complete: string;
      };
      AnonymousDashboard: {
        RegistrationRequired: string;
        Register: string;
      };
      Redeem: {
        Step1: string;
        Step2: string;
        NoCardYet: string;
      };
      RedeemPublic: {
        Step1: string;
        Step2: string;
      };
      LeadInvitation: {
        Description: string;
      };
      ColdWallet: {
        WithdrawalTitle: string;
        DepositTitle: string;
        GenerateAddress: string;
      };
      SystemReport: {
        HotWallet: string;
        ColdWallet: string;
        ExchangeWithdrawal: string;
        UserAccounts: string;
        ControlSum: string;
        SystemWalletWithdrawal: string;
        ExchangeWithdrawalFee: string;
        BalanceOnExchange: string;
        TradingVolume: string;
        PerMonth: string;
        PerWeek: string;
        PurchasedBTC: string;
        WithdrawnOrPending: string;
        BTCLedgerSum: string;
        CZKLedgerSum: string;
        SystemVersion: string;
      };
      BankingAuth: {
        BankListTitle: string;
        BanksInPreparation: string;
      };
      Account: {
        ProfileError: string;
      };
      Error403: {
        Logout: string;
      };
      CustomerAnonymousPaymentPay: {
        SignIn: string;
        ConfirmBox: {
          Title: string;
          Description: string;
          CreateAccountButton: string;
          CloseButton: string;
        };
        TermsAgreement: { text: string; link: string; linkText: string };
        Steps: {
          detail: string;
          bank: string;
          account: string;
        };
        BankSelection: {
          InfoBox: string;
          WarningBox: string;
        };
        AccountSelection: {
          InfoBox: string;
        };
      };
      NotificationsManagement: {
        NewNotification: string;
        PlannedNotifications: string;
        SentNotifications: string;
        DisabledNotifications: string;
        NoNotifications: string;
      };
      NotificationForm: {
        Name: string;
        Description: string;
        NotificationType: string;
        Duration: {
          FromDate: string;
          FromTime: string;
          ToDate: string;
          ToTime: string;
        };
        DurationType: {
          Dismiss: string;
          Period: string;
        };
        DurationPrompt: string;
        Link: string;
        Create: string;
        Preview: string;
        Filters: {
          Banks: string;
          BanksPlaceholder: string;
          UserTypes: string;
          UserTypesPlaceholder: string;
        };
      };
    };
  };
  MobileApp: {
    InstallBanner: {
      Title: string;
      Description: string;
      AppStoreBadgeAlt: string;
      PlayStoreBadgeAlt: string;
    };
  };
  Language: {
    Czech: string;
    Settings: string;
    Language: string;
  };
  FormLabels: {
    AccountType: string;
    TransferAccountType: string;
    RecipientIBAN: string;
    Amount: string;
  };
  TransactionErrors: {
    UnknownError: string;
    UnknownPaymentError: string;
    TransactionNotFound: string;
    BankTemporarilyUnavailable: string;
    BankConnectionExpired: string;
    TransactionNotFoundOrNotAccessible: string;
    InsufficientBalance: string;
    BalanceVerificationFailed: string;
    SameAccountError: string;
    InvalidAccountCurrency: string;
    InvalidTransactionCurrency: string;
    TransactionTypeNotAllowed: string;
    TransactionLimitExceeded: string;
    UnspecifiedError: string;
    TransactionProcessingFailed: string;
    UnspecifiedPaymentError: string;
    BankAccount: string;
    User: string;
    InvalidBankAccountForReceivingPayment: string;
    Status: string;
    TransactionAlreadyCompleted: string;
    TransactionCancelled: string;
    TransactionExpired: string;
    ActiveTransactionExists: string;
    AuthLimitExceeded: string;
    InvalidJsonFormat: string;
    GeneralRejection: string;
    NotUniqueTransactionReference: string;
    InvalidCharacterSet: string;
    InvalidAccountNumber: string;
    FieldInvalid: string;
    FieldMissing: string;
    TransactionAmountForbidden: string;
    AccountInvalid: string;
    TransactionDecimalNumbersForbidden: string;
    InvalidDate: string;
    TransactionInvalidIBAN: string;
    InstantPaymentsNotAvailable: string;
    TransactionNotSignable: string;
    DebtorBankAccountForbidden: string;
    PaymentLimitExceeded: string;
    PaymentRejectedByBank: string;
    PaymentCancelledByUser: string;
    PaymentExpired: string;
    PaymentUnauthorized: string;
    PaymentTechnicalError: string;
    DebtorCreditorAccountIdentical: string;
    TransactionLimits: {
      generatedTransactionLimitExceeded: string;
      oneTimeSendLimitExceeded: string;
      dailySendLimitExceeded: string;
      generatedDailySendLimitExceeded: string;
      monthlySendLimitExceeded: string;
      generatedMonthlySendLimitExceeded: string;
      generatedTransactionLimitExceededIncompleteRegistration: string;
      oneTimeSendLimitExceededIncompleteRegistration: string;
      dailySendLimitExceededIncompleteRegistration: string;
      generatedDailySendLimitExceededIncompleteRegistration: string;
      monthlySendLimitExceededIncompleteRegistration: string;
      generatedMonthlySendLimitExceededIncompleteRegistration: string;
      oneTimeReceiveLimitExceededPreApproval: string;
      cumulativeReceiveLimitExceededPreApproval: string;
      currencyNotSupportedPreApproval: string;
      unspecifiedError: string;
    };
  };
  AML: {
    BasicInfo: string;
    CompanyName: string;
    FirstName: string;
    LastName: string;
    CompanyNumber: string;
    TaxNumber: string;
    PersonalNumber: string;
    BirthDate: string;
    BirthPlace: string;
    Nationality: string;
    Gender: string;
    Phone: string;
    Email: string;
    Unknown: string;
    Documents: string;
    DocumentNumber: string;
    DocumentIssuer: string;
    DocumentValidity: string;
    DocumentType: string;
    NoDocuments: string;
    IdentificationImageType: {
      FirstIDCardFrontSide: string;
      FirstIDCardBackSide: string;
      SecondIDCardFrontSide: string;
      SecondIDCardBackSide: string;
    };
    PdfUploaded: string;
    OpenPdf: string;
    ActingPerson: string;
    BeneficialOwners: string;
    UboUnknownAlert: string;
    Loading: string;
  };
  PersonalInfo: {
    ConfirmDataRequest: string;
    FillMissingData: string;
    DataConfirmationMessage: string;
  };
  Nfc: {
    NfcCard: string;
    InvalidNfcCardNoFormat: string;
    NfcCardNo: string;
    CopyNfcCardNo: string;
    CopyNfcCardNoSuccess: string;
    CardNotAssigned: string;
    AssignToPaypoint: string;
    SelectPaypoint: string;
    SelectMerchant: string;
    CardOverwriteConfirmation: string;
    CardAssignedSuccessfully: string;
    CardAssignmentFailed: string;
    CardAssignmentFailedFormatError: string;
    Redirecting: string;
    RedirectFallbackHint: string;
    RedirectManually: string;
    RedirectNotConfigured: string;
  };
  ExternalIntegrations: {
    Title: string;
    Description: string;
    DownloadPlugin: string;
    DownloadDocumentation: string;
    WooCommerce: {
      Name: string;
      Description: string;
      PluginUrl: string;
      DocumentationUrl: string;
    };
    PrestaShop: {
      Name: string;
      Description: string;
      PluginUrl: string;
      DocumentationUrl: string;
    };
    PaymentGateway: {
      Name: string;
      Description: string;
      DocumentationUrl: string;
    };
  };
  Paypoint: {
    Paypoint: string;
    NoPaypoint: string;
    UnnamedPaypoint: string;
    NoPaypointPlaceholder: string;
    AddPaypointButton: string;
    DownloadQRCode: string;
    Individual: string;
    SeeOptions: string;
    IndividualUnderConstruction: string;
    NewPaypoint: string;
    UpdatePaypoint: string;
    IdlePaypointInfo: string;
    BackToDashboard: string;
    LoadingError: string;
    UnknownOwner: string;
    Form: {
      Name: string;
      Description: string;
      Legend: {
        GeneralInfo: string;
        NFC: string;
        PaypointSettings: string;
        Device: string;
        PaypointType: string;
      };
      PaymentTypeLabel: string;
      PaymentType: {
        Standard: { label: string; tooltip: string };
        FixedPrice: { label: string; tooltip: string };
        EditablePrice: { label: string; tooltip: string };
        NfcLink: { label: string; tooltip: string };
        NfcTableService: { label: string; tooltip: string };
      };
      FixedAmountLabel: string;
      CurrencyLabel: string;
      FixedAmountRequired: string;
      CurrencyRequired: string;
      TipsLabel: string;
      TipsCheckbox: string;
      GatewaySettingsHint: string;
      RedirectUrlLabel: string;
      RedirectUrlInvalid: string;
      TableServiceUrlMissing: string;
      AssignNfcCardNoConflict: string;
      NfcCardNo: string;
      NfcCardNoHelperText: string;
      Create: string;
      NfcCardNoFormatError: string;
      NameMaxLengthError: string;
      BankAccountNotAccessible: string;
      EmployeesNotAccessible: string;
      Employees: string;
      PaypointAccount: string;
      Facility: string;
      Type: string;
      TypeNFC: string;
      TypeTerminal: string;
      TypeGateway: string;
      TypeGatewayShort: string;
      Device: {
        NoActiveDevice: string;
        LinkDevice: string;
        LinkDeviceTitle: string;
        ActiveDevice: string;
      };
      Activation: {
        Legend: string;
        Code: string;
        HelperText: string;
        Copy: string;
        Copied: string;
      };
    };
  };
  Sentry: {
    FormTitle: string;
    NameLabel: string;
    EmailLabel: string;
    MessageLabel: string;
    MessageCharCount: string;
    CancelButtonLabel: string;
    SubmitButtonLabel: string;
    NamePlaceholder: string;
    EmailPlaceholder: string;
    MessagePlaceholder: string;
    SuccessMessageText: string;
    ErrorMessageText: string;
    Validation: {
      MessageRequired: string;
      MessageTooShort: string;
      EmailInvalid: string;
    };
  };
  Support: {
    ButtonLabel: string;
    /** How to reach support, independent of the screen asking — the Support dialog and the
        Contact page both read these. In the dictionaries rather than in a constant so a market
        can carry its own. */
    Contact: {
      PhoneNumber: string;
      Email: string;
      /** Hunters are served under the Legi.one brand, so they get their own address. */
      HunterEmail: string;
    };
    Choice: {
      Title: string;
      Description: string;
      TechnicalProblemButton: string;
      TechnicalProblemDescription: string;
      TechnicalProblemUnavailable: string;
      SupportRequestButton: string;
      SupportRequestDescription: string;
      UrgentHelp: string;
      CallPhone: string;
    };
    Request: {
      Title: string;
      CategoryLabel: string;
      CategoryPlaceholder: string;
      SubjectLabel: string;
      DescriptionLabel: string;
      DescriptionCharCount: string;
      AttachmentLabel: string;
      AttachmentPlaceholder: string;
      AttachmentRemove: string;
      NameLabel: string;
      EmailLabel: string;
      SubmitButton: string;
      CancelButton: string;
      Categories: {
        ClientOnboarding: string;
        CommissionsAndPayouts: string;
        ValorPoints: string;
        TechnicalIssues: string;
        StardustPoints: string;
        PaymentIssues: string;
        AccountAndSignIn: string;
        TransactionComplaints: string;
        HardwareAndConnectivity: string;
        FinanceAndSettlement: string;
        AccountAdministration: string;
        Other: string;
      };
      Validation: {
        CategoryRequired: string;
        SubjectRequired: string;
        DescriptionRequired: string;
        DescriptionTooShort: string;
        NameRequired: string;
        EmailRequired: string;
        EmailInvalid: string;
        AttachmentTooLarge: string;
        AttachmentTypeNotSupported: string;
      };
      SuccessMessage: string;
      ErrorMessage: string;
      RateLimitedMessage: string;
    };
    /** DSA Art. 16 notice form (ZS-1743) — reporting a profile or a chat message. */
    ContentReport: {
      Title: string;
      PickerButton: string;
      PickerDescription: string;
      /** What the notice is about, shown above the form. */
      TargetProfile: string;
      TargetMessage: string;
      /** The anonymous picker path asks for the handle; messages are reported from the chat itself. */
      HandleLabel: string;
      HandlePlaceholder: string;
      MessageHint: string;
      CategoryLabel: string;
      CategoryPlaceholder: string;
      ExplanationLabel: string;
      ExplanationHelper: string;
      NameLabel: string;
      EmailLabel: string;
      ContactOptionalNote: string;
      BonaFideLabel: string;
      LegalNote: string;
      SubmitButton: string;
      CancelButton: string;
      Categories: {
        ChildSexualAbuse: string;
        ThreatsOrViolence: string;
        HateSpeech: string;
        FraudOrScam: string;
        Impersonation: string;
        PersonalDataOrPrivacy: string;
        IntellectualProperty: string;
        OtherIllegal: string;
        HarassmentOrBullying: string;
        SpamOrUnwantedContact: string;
        OtherTermsViolation: string;
      };
      Validation: {
        CategoryRequired: string;
        HandleRequired: string;
        ExplanationRequired: string;
        ExplanationTooShort: string;
        NameRequired: string;
        EmailRequired: string;
        EmailInvalid: string;
        BonaFideRequired: string;
      };
      SuccessMessage: string;
      SuccessMessageWithReference: string;
      ErrorMessage: string;
      NotFoundMessage: string;
      SelfReportMessage: string;
      RateLimitedMessage: string;
    };
    Result: {
      ErrorTitle: string;
    };
  };
  Employee: {
    EmployeeStatus: {
      Inactive: string;
      InvitedWaitingForConfirmation: string;
      Active: string;
      Created: string;
    };
    Detail: {
      Title: string;
      NoEmployeeSelectedError: string;
      EmployeeNotFoundError: string;
      SendAccess: string;
      AccessSent: string;
      CancelInvitation: string;
      CancelInvitationConfirmText: string;
      RemoveEmployee: string;
      RemoveEmployeeConfirmText: string;
      RemovedInfo: string;
      SaveError: string;
      SaveSuccess: string;
    };
    List: {
      LoadingError: string;
      AddEmployee: string;
      FilterLabels: Record<EmployeeStatus, string>;
    };
    Dashboard: {
      Title: string;
    };
    KeypadPayment: {
      Title: string;
      ComingSoon: string;
    };
    Create: {
      Title: string;
      CreateButton: string;
      CreateError: string;
      CreateSuccess: string;
      MissingMerchantID: string;
      Legend: string;
    };
  };
  Campaigns: {
    // CampaignSpecific: {
    //   helppes: {
    //     Title: string;
    //     Description: string;
    //     LearnMoreLink: string;
    //     Url: string;
    //     PageTitle: string;
    //     LogoAlt: string;
    //     InfoCards: {
    //       HelpingNeedy: {
    //         Title: string;
    //         Description: string;
    //       };
    //       TestingPayment: {
    //         Title: string;
    //         Description: string;
    //       };
    //       CreateAccountAndHelp: {
    //         Title: string;
    //         Description: string;
    //       };
    //     };
    //     DonationForm: {
    //       AmountLabel: string;
    //       SubmitButton: string;
    //       AmountRequired: string;
    //       TransactionError: string;
    //       PaymentReference: string;
    //       NoteForCreditor: string;
    //     };
    //     TimelineSteps: {
    //       Donate: {
    //         Title: string;
    //       };
    //       JoinTeam: {
    //         Title: string;
    //         Description: string;
    //       };
    //       PayAtCafe: {
    //         Title: string;
    //         Description: string;
    //       };
    //       AnyMethod: {
    //         Title: string;
    //         Description: string;
    //       };
    //     };
    //   };
    //   sanitka: {
    //     Title: string;
    //     Description: string;
    //     LearnMoreLink: string;
    //     Url: string;
    //     PageTitle: string;
    //     LogoAlt: string;
    //     InfoCards: {
    //       HelpingNeedy: {
    //         Title: string;
    //         Description: string;
    //       };
    //       TestingPayment: {
    //         Title: string;
    //         Description: string;
    //       };
    //       CreateAccountAndHelp: {
    //         Title: string;
    //         Description: string;
    //       };
    //     };
    //     DonationForm: {
    //       AmountLabel: string;
    //       SubmitButton: string;
    //       AmountRequired: string;
    //       TransactionError: string;
    //       PaymentReference: string;
    //       NoteForCreditor: string;
    //     };
    //     TimelineSteps: {
    //       Donate: {
    //         Title: string;
    //       };
    //       JoinTeam: {
    //         Title: string;
    //         Description: string;
    //       };
    //       PayAtCafe: {
    //         Title: string;
    //         Description: string;
    //       };
    //       AnyMethod: {
    //         Title: string;
    //         Description: string;
    //       };
    //     };
    //   };
    //   kolibrici: {
    //     Title: string;
    //     Description: string;
    //     LearnMoreLink: string;
    //     Url: string;
    //     PageTitle: string;
    //     LogoAlt: string;
    //     InfoCards: {
    //       HelpingNeedy: {
    //         Title: string;
    //         Description: string;
    //       };
    //       TestingPayment: {
    //         Title: string;
    //         Description: string;
    //       };
    //       CreateAccountAndHelp: {
    //         Title: string;
    //         Description: string;
    //       };
    //     };
    //     DonationForm: {
    //       AmountLabel: string;
    //       SubmitButton: string;
    //       AmountRequired: string;
    //       TransactionError: string;
    //       PaymentReference: string;
    //       NoteForCreditor: string;
    //     };
    //     TimelineSteps: {
    //       Donate: {
    //         Title: string;
    //       };
    //       JoinTeam: {
    //         Title: string;
    //         Description: string;
    //       };
    //       PayAtCafe: {
    //         Title: string;
    //         Description: string;
    //       };
    //       AnyMethod: {
    //         Title: string;
    //         Description: string;
    //       };
    //     };
    //   };
    //   "ochrana-zvirat": {
    //     Title: string;
    //     Description: string;
    //     LearnMoreLink: string;
    //     Url: string;
    //     PageTitle: string;
    //     LogoAlt: string;
    //     InfoCards: {
    //       HelpingNeedy: {
    //         Title: string;
    //         Description: string;
    //       };
    //       TestingPayment: {
    //         Title: string;
    //         Description: string;
    //       };
    //       CreateAccountAndHelp: {
    //         Title: string;
    //         Description: string;
    //       };
    //     };
    //     DonationForm: {
    //       AmountLabel: string;
    //       SubmitButton: string;
    //       AmountRequired: string;
    //       TransactionError: string;
    //       PaymentReference: string;
    //       NoteForCreditor: string;
    //     };
    //     TimelineSteps: {
    //       Donate: {
    //         Title: string;
    //       };
    //       JoinTeam: {
    //         Title: string;
    //         Description: string;
    //       };
    //       PayAtCafe: {
    //         Title: string;
    //         Description: string;
    //       };
    //       AnyMethod: {
    //         Title: string;
    //         Description: string;
    //       };
    //     };
    //   };
    //   mcdonald: {
    //     Title: string;
    //     Description: string;
    //     LearnMoreLink: string;
    //     Url: string;
    //     PageTitle: string;
    //     LogoAlt: string;
    //     InfoCards: {
    //       HelpingNeedy: {
    //         Title: string;
    //         Description: string;
    //       };
    //       TestingPayment: {
    //         Title: string;
    //         Description: string;
    //       };
    //       CreateAccountAndHelp: {
    //         Title: string;
    //         Description: string;
    //       };
    //     };
    //     DonationForm: {
    //       AmountLabel: string;
    //       SubmitButton: string;
    //       AmountRequired: string;
    //       TransactionError: string;
    //       PaymentReference: string;
    //       NoteForCreditor: string;
    //     };
    //     TimelineSteps: {
    //       Donate: {
    //         Title: string;
    //       };
    //       JoinTeam: {
    //         Title: string;
    //         Description: string;
    //       };
    //       PayAtCafe: {
    //         Title: string;
    //         Description: string;
    //       };
    //       AnyMethod: {
    //         Title: string;
    //         Description: string;
    //       };
    //     };
    //   };
    //   "kroky-k-pochopeni": {
    //     Title: string;
    //     Description: string;
    //     LearnMoreLink: string;
    //     Url: string;
    //     PageTitle: string;
    //     LogoAlt: string;
    //     InfoCards: {
    //       HelpingNeedy: {
    //         Title: string;
    //         Description: string;
    //       };
    //       TestingPayment: {
    //         Title: string;
    //         Description: string;
    //       };
    //       CreateAccountAndHelp: {
    //         Title: string;
    //         Description: string;
    //       };
    //     };
    //     DonationForm: {
    //       AmountLabel: string;
    //       SubmitButton: string;
    //       AmountRequired: string;
    //       TransactionError: string;
    //       PaymentReference: string;
    //       NoteForCreditor: string;
    //     };
    //     TimelineSteps: {
    //       Donate: {
    //         Title: string;
    //       };
    //       JoinTeam: {
    //         Title: string;
    //         Description: string;
    //       };
    //       PayAtCafe: {
    //         Title: string;
    //         Description: string;
    //       };
    //       AnyMethod: {
    //         Title: string;
    //         Description: string;
    //       };
    //     };
    //   };
    // };
    Campaign: {
      DefaultHeader: string;
      DefaultLearnMore: string;
      DonationForm: {
        AmountLabel: string;
        AmountRequired: string;
        AmountMin: string;
        SubmitButton: string;
        TransactionError: string;
      };
      Page: {
        Loading: string;
        CampaignNotFound: string;
        CampaignNotFoundMessage: string;
        StatusNotStarted: string;
        StatusNotStartedWithDate: string;
        StatusNotStartedWithoutDate: string;
        StatusFinished: string;
        StatusFinishedWithDate: string;
        StatusFinishedWithoutDate: string;
        EndsOn: string;
        DonationsNotAvailable: string;
      };
      CreateAccountButton: string;
      InfoCards: {
        HelpingNeedy: {
          Title: string;
          Description: string;
        };
        TestingPayment: {
          Title: string;
          Description: string;
        };
        CreateAccountAndHelp: {
          Title: string;
          Description: string;
        };
      };
      TimelineSteps: {
        Donate: {
          Title: string;
        };
        JoinTeam: {
          Title: string;
          Description: string;
        };
        PayAtCafe: {
          Title: string;
          Description: string;
        };
        AnyMethod: {
          Title: string;
          Description: string;
        };
      };
    };
  };
  Raffle: {
    Notifications: WithPlurals<{
      Title: string;
      Registration: string;
      Referral: string;
      Payment: string;
    }>;
    Dashboard: WithPlurals<{
      Title: string;
      TicketCount: string;
      DrawDate: string;
      DateToBeAnnounced: string;
      ShowDetail: string;
      CompetitionsRunning: string;
    }>;
    Page: WithPlurals<{
      Title: string;
      NotRunning: string;
      LoadError: string;
      MainDraw: string;
      MyTickets: string;
      DrawDate: string;
      Rewards: string;
      History: string;
      HistoryEmpty: string;
      HistoryTickets: string;
      Drawn: string;
      MoveTickets: string;
    }>;
    MoveDialog: WithPlurals<{
      Title: string;
      TargetLabel: string;
      CountLabel: string;
      CountHelper: string;
      Submit: string;
      MovedToast: string;
      MoveError: string;
      NothingMoved: string;
      CompetitionsChanged: string;
    }>;
  };
  Chat: {
    Title: string;
    FindPeople: string;
    ReadBy: string;
    SelectConversation: string;
    ClosedConversation: string;
    Unavailable: string;
    DisclosedNamesUnavailable: string;
    /** Escape hatch of the chat onboarding gate (handle + phone steps). */
    BackToDashboard: string;
    /** iOS shell only: device-level opt-in for Stream chat pushes (Settings row). */
    PushNotifications: {
      DeviceToggleLabel: string;
      DeviceToggleDescription: string;
      DeniedHint: string;
    };
    Thread: {
      Title: string;
    };
    FindInConversation: {
      MenuItem: string;
      Placeholder: string;
      Counter: string;
      NoMatches: string;
      Older: string;
      Newer: string;
      Close: string;
      JumpError: string;
      SearchError: string;
    };
    HandleGate: {
      Title: string;
      Description: string;
    };
    Reactions: {
      Title: string;
    };
    PhoneGate: {
      Title: string;
      DescriptionVerify: string;
      DescriptionAdd: string;
      PhoneLabel: string;
      SendCode: string;
      CodeSent: string;
      Verify: string;
      EditPhone: string;
      ResendCode: string;
      SmsFailed: string;
      TooManyAttempts: string;
      WrongCode: string;
      VerifySuccess: string;
    };
    Layout: {
      SearchPlaceholder: string;
    };
    Categories: {
      All: string;
      Mutual: string;
      Shopping: string;
      Merchants: string;
      Groups: string;
      Requests: string;
    };
    NoChannels: string;
    NoSearchResults: string;
    NewChat: {
      Title: string;
      DirectTitle: string;
      CreateGroupWith: string;
      GroupTitle: string;
      OpenError: string;
      PrivateSenderNotice: string;
      RemoveMember: string;
      SearchPlaceholder: string;
      MembersLabel: string;
      SuggestionsLabel: string;
      ResultsLabel: string;
      EligibilityLabel: string;
      EligibilityAnyone: string;
      EligibilityFullMooneOnly: string;
      FullMooneOnlyBadge: string;
      CreateError: string;
      CreateIneligibleMemberError: string;
      CreateNoHandleMemberError: string;
      IneligibleSelectedWarning: string;
      CustomerSelectedWarning: string;
      NoHandleSelectedWarning: string;
      GroupNameLabel: string;
      ChannelNamePlaceholder: string;
      NoResults: string;
      UnknownUser: string;
      Cancel: string;
      Create: string;
    };
    Members: {
      Title: string;
      You: string;
      AddTitle: string;
      Add: string;
      Cancel: string;
      AddError: string;
      Remove: string;
      RemoveError: string;
      Owner: string;
      AlreadyMember: string;
      NotFullMoone: string;
      CustomerWritesFirst: string;
      NoHandle: string;
      LeaveChannel: string;
      LeaveConfirmTitle: string;
      LeaveConfirmMessage: string;
      Leave: string;
      LeaveError: string;
    };
    Roles: {
      Customer: string;
      Merchant: string;
      Hunter: string;
      HunterPlus: string;
      Admin: string;
      CustomerOnboarding: string;
    };
    Rename: {
      Title: string;
      Save: string;
      Cancel: string;
      Error: string;
    };
    Requests: {
      Accept: string;
      Reject: string;
      RequestMessage: string;
      AcceptError: string;
      RejectError: string;
      ShowRejected: string;
      ShowPending: string;
      Join: string;
      JoinError: string;
      RejectedMessage: string;
    };
    PaymentRequest: {
      Pending: string;
      Pay: string;
      Drawer: {
        Title: string;
        BankAccount: string;
        Amount: string;
        Note: string;
        NotePlaceholder: string;
        Submit: string;
        Error: string;
        InvalidCurrency: string;
        Validation: {
          SelectBankAccount: string;
          EnterAmount: string;
          AmountMustBePositive: string;
        };
      };
    };
    DirectPayment: {
      Sent: string;
      Received: string;
      Drawer: {
        Title: string;
        BankAccount: string;
        Amount: string;
        Note: string;
        NotePlaceholder: string;
        Submit: string;
        Error: string;
        RecipientNoPrimaryAccount: string;
        InvalidCurrency: string;
        RecipientCurrencyNotSupported: string;
        RecipientLimitExceeded: string;
        NotChannelMember: string;
        MessageNotPosted: string;
      };
    };
    MultiPayerRequest: {
      Description: string;
      ThreadTitle: string;
      Pay: string;
      Close: string;
      CloseError: string;
      Closed: string;
      Unavailable: string;
      Payments: WithPlurals<{
        Count: string;
      }>;
      Drawer: {
        Title: string;
        Amount: string;
        Hint: string;
        Submit: string;
        Error: string;
      };
    };
    PaymentMenu: {
      DirectPayment: string;
      PaymentRequest: string;
      LiteRestriction: string;
      LiteBankRestriction: string;
      LitePayUnavailable: string;
      RecipientNotFullMoone: string;
    };
    TransactionStatus: {
      Created: string;
      Initiated: string;
      Processing: string;
      Success: string;
      Fail: string;
      Cancelled: string;
      Unknown: string;
    };
    Preview: {
      PaymentRequest: string;
      DirectPayment: string;
    };
    PushCaption: {
      PaymentRequest: string;
      DirectPaymentSent: string;
    };
  };
  Admin: {
    Terminals: {
      PageTitle: string;
      Table: {
        SerialNumber: string;
        Tid: string;
        Mid: string;
        HardwareModel: string;
        Status: string;
        Financing: string;
        Hunter: string;
        Merchant: string;
        Facility: string;
        PurchasePrice: string;
        LastChangedAt: string;
        Ship: string;
      };
      Status: {
        InStock: string;
        AssignedToHunter: string;
        AssignedToMerchant: string;
        Shipped: string;
        WithMerchant: string;
        Deployed: string;
        Returned: string;
      };
      Financing: {
        Moone: string;
        Hunter: string;
      };
      Form: {
        SerialNumber: string;
        HardwareModel: string;
        PurchasePrice: string;
        Currency: string;
        Status: string;
        AddTitle: string;
        Save: string;
        Cancel: string;
        Delete: string;
      };
      AssignHunter: {
        HunterLabel: string;
        SearchHint: string;
        NoOptions: string;
      };
      Validation: {
        SerialNumberRequired: string;
        HardwareModelRequired: string;
      };
      Audit: {
        Title: string;
        Empty: string;
        When: string;
        Field: string;
        Change: string;
        Who: string;
      };
      Messages: {
        LoadError: string;
        UpdateSuccess: string;
        CreateSuccess: string;
        UpdateError: string;
        DuplicateSerialNumber: string;
        DeleteSuccess: string;
        DeleteError: string;
        DeleteConfirm: string;
        ShipSuccess: string;
        ShipError: string;
      };
    };
    SsoClients: {
      PageTitle: string;
      CreateButton: string;
      Table: {
        ClientId: string;
        DisplayName: string;
        Type: string;
        Confidential: string;
        Public: string;
        RedirectUris: string;
        AllowedRoles: string;
        AllowedScopes: string;
        Actions: string;
        RotateSecret: string;
        Delete: string;
      };
      Form: {
        CreateTitle: string;
        EditTitle: string;
        ClientId: string;
        DisplayName: string;
        IsConfidential: string;
        IsConfidentialHint: string;
        RedirectUris: string;
        RedirectUrisHint: string;
        PostLogoutRedirectUris: string;
        AllowedRoles: string;
        AllowedRolesHint: string;
        AllowedScopes: string;
        AllowedScopesHint: string;
        ListPlaceholder: string;
        Submit: string;
        Save: string;
        Cancel: string;
      };
      Validation: {
        ClientIdInvalid: string;
        DisplayNameRequired: string;
        RedirectUriRequired: string;
        RedirectUriInvalid: string;
        ScopeRequiresRole: string;
      };
      Scopes: {
        Payments: string;
        Merchant: string;
        Hunter: string;
        TerminalsManage: string;
        DirectoryRead: string;
        CouponsCreate: string;
        LoyaltyRead: string;
      };
      Secret: {
        Title: string;
        Warning: string;
        Label: string;
        Copy: string;
        Copied: string;
        Close: string;
      };
      Messages: {
        LoadError: string;
        CreateSuccess: string;
        CreateError: string;
        ClientIdTaken: string;
        UpdateSuccess: string;
        UpdateError: string;
        DeleteConfirmTitle: string;
        DeleteConfirm: string;
        DeleteSuccess: string;
        DeleteError: string;
        RotateConfirmTitle: string;
        RotateConfirm: string;
        RotateError: string;
      };
    };
    Coupons: {
      PageTitle: string;
      CreateButton: string;
      Table: {
        Code: string;
        Region: string;
        Discount: string;
        DiscountType: string;
        Expiration: string;
        Usage: string;
        Status: string;
        Delete: string;
        CopyCode: string;
        CodeCopied: string;
      };
      Status: {
        Active: string;
        Expired: string;
        OutOfStock: string;
      };
      Form: {
        DialogTitle: string;
        Code: string;
        Region: string;
        AutoGenerate: string;
        Discount: string;
        DiscountType: string;
        Percentage: string;
        FixedAmount: string;
        Expiration: string;
        Stock: string;
        StockHelp: string;
        Submit: string;
        Save: string;
        Cancel: string;
        EditTitle: string;
        EditTitleWithCode: string;
      };
      Validation: {
        CodeRequired: string;
        DiscountRequired: string;
        DiscountPositive: string;
        DiscountMax100: string;
        ExpirationRequired: string;
        StockRequired: string;
        StockMin: string;
      };
      Messages: {
        CreateSuccess: string;
        CreateError: string;
        LoadError: string;
        DeleteSuccess: string;
        DeleteError: string;
        DeleteConfirmTitle: string;
        DeleteConfirm: string;
        UpdateSuccess: string;
        UpdateError: string;
      };
    };
    Raffle: {
      PageTitle: string;
      CreateButton: string;
      Table: {
        Region: string;
        Name: string;
        Type: string;
        TypeDefault: string;
        TypeCompetition: string;
        Status: string;
        DrawDate: string;
        Tickets: string;
        Participants: string;
        Checksum: string;
        ChecksumHelp: string;
        ChecksumCopied: string;
        AllocationChecksum: string;
        AllocationChecksumHelp: string;
        AllocationChecksumCopied: string;
        Empty: string;
        Unscheduled: string;
      };
      Status: {
        Draft: string;
        Announced: string;
        Closed: string;
        Drawn: string;
        Cancelled: string;
      };
      Actions: {
        Edit: string;
        View: string;
        Announce: string;
        Close: string;
        Complete: string;
        Export: string;
        FindHolder: string;
        Cancel: string;
        SetDefault: string;
      };
      Form: {
        CreateTitle: string;
        EditTitle: string;
        Region: string;
        Name: string;
        DrawDate: string;
        Rewards: string;
        AddReward: string;
        RewardTitle: string;
        RewardDescription: string;
        RewardQuantity: string;
        RemoveReward: string;
        Submit: string;
        Cancel: string;
        MainRoundWarning: string;
      };
      Validation: {
        NameRequired: string;
        RewardTitleRequired: string;
        RegionRequired: string;
        RewardQuantityInvalid: string;
        TicketNumberInvalid: string;
        TicketNumberRange: string;
      };
      Messages: {
        LoadError: string;
        CreateSuccess: string;
        CreateError: string;
        UpdateSuccess: string;
        UpdateError: string;
        UpdateConflict: string;
        AnnounceSuccess: string;
        AnnounceError: string;
        AnnounceConfirmTitle: string;
        AnnounceConfirm: string;
        AnnounceRequirements: string;
        CloseSuccess: string;
        CloseError: string;
        CloseConfirmTitle: string;
        CloseConfirm: string;
        CloseCompetitionConfirm: string;
        CompleteSuccess: string;
        CompleteError: string;
        CompleteConfirmTitle: string;
        CompleteConfirm: string;
        CancelSuccess: string;
        CancelError: string;
        CancelConfirmTitle: string;
        CancelConfirm: string;
        SetDefaultSuccess: string;
        SetDefaultError: string;
        SetDefaultConfirmTitle: string;
        SetDefaultConfirm: string;
        ExportError: string;
        ExportIntegrityError: string;
      };
      HolderLookup: {
        Title: string;
        TicketNumber: string;
        Search: string;
        HolderName: string;
        Email: string;
        TicketCount: string;
        Range: string;
        NotFound: string;
        SealMismatch: string;
        Error: string;
      };
    };
    Campaigns: {
      PageTitle: string;
      CreatePageTitle: string;
      CreateButton: string;
      DraftCampaigns: string;
      PublishedCampaigns: string;
      ArchivedCampaigns: string;
      Table: {
        CampaignName: string;
        Code: string;
        Receiver: string;
        NoReceiverAssigned: string;
        Affiliate: string;
        Enabled: string;
        Disabled: string;
        Status: string;
        StatusOnRestore: string;
        StartDate: string;
        EndDate: string;
        Unlimited: string;
        Created: string;
        Deleted: string;
        Actions: string;
        ViewCampaign: string;
        Edit: string;
        Delete: string;
        Restore: string;
        CopyUrl: string;
        UrlCopied: string;
      };
      Status: {
        Draft: string;
        DraftDescription: string;
        Archived: string;
        ArchivedDescription: string;
        ActiveUnlimited: string;
        UnlimitedDescription: string;
        StartsToday: string;
        StartsTomorrow: string;
        StartsInDays: string;
        StartsOn: string;
        Finished: string;
        EndedOn: string;
        EndsToday: string;
        DayLeft: string;
        DaysLeft: string;
        EndsOn: string;
        Active: string;
        CurrentlyRunning: string;
        NotStarted: string;
      };
      Form: {
        BasicInformation: string;
        CampaignName: string;
        CampaignNamePlaceholder: string;
        CampaignCode: string;
        CampaignCodePlaceholder: string;
        CampaignCodeHelperCreate: string;
        CampaignCodeHelperEdit: string;
        Description: string;
        DescriptionPlaceholder: string;
        DescriptionCharCount: string;
        EnableAffiliateProgram: string;
        CampaignValidity: string;
        DateModeUnlimited: string;
        DateModeUnlimitedDescription: string;
        DateModeFromTo: string;
        DateModeFromToDescription: string;
        StartDate: string;
        EndDate: string;
        ThankYouSection: string;
        Color: string;
        ColorHelp: string;
        PaymentInstructionsText: string;
        PaymentInstructionsTextPlaceholder: string;
        PaymentInstructionsTextHelper: string;
        OnlyWithMooneText: string;
        OnlyWithMooneTextPlaceholder: string;
        OnlyWithMooneTextHelper: string;
        UnlimitedDateInfo: string;
        PaymentReceiver: string;
        PaymentReceiverDescription: string;
        MerchantUser: string;
        CampaignContent: string;
        HeaderTitle: string;
        HeaderTitlePlaceholder: string;
        FoundationLinkName: string;
        FoundationLinkNamePlaceholder: string;
        FoundationLinkNameHelper: string;
        FoundationLinkUrl: string;
        FoundationLinkUrlPlaceholder: string;
        FoundationLinkUrlHelper: string;
        SuccessfulPaymentText: string;
        SuccessfulPaymentTextPlaceholder: string;
        SuccessfulPaymentTextHelper: string;
        StepBasicInfo: string;
        StepLandingPage: string;
        StepThankYouPage: string;
        StepValidity: string;
        PreviewCampaignPage: string;
        PreviewThankYouPage: string;
        Images: string;
        Logo: string;
        LogoHelper: string;
        LandingImage: string;
        LandingImageHelper: string;
        ThankYouImage: string;
        ThankYouImageHelper: string;
        Icon: string;
        IconHelper: string;
        ImageUploadSuccess: string;
        ImageUploadError: string;
        // Keys match backend CampaignErrors codes (ResultErrors.cs) — accessed via UploadError[code]
        UploadError: {
          invalidFileType: string;
          fileSizeExceeded: string;
          fileContentMismatch: string;
        };
        ImageDeleteSuccess: string;
        ImageDeleteError: string;
        CreateButton: string;
        UpdateButton: string;
        UpdateLandingPageButton: string;
        UpdateThankYouPageButton: string;
        NextStepButton: string;
        PreviousStepButton: string;
        SaveChangesButton: string;
        SaveButton: string;
        CloseButton: string;
        PublishButton: string;
        PublishRequiresReceiver: string;
        ArchiveButton: string;
        UnpublishButton: string;
        CampaignIsLive: string;
        Validation: {
          NameRequired: string;
          CodeRequired: string;
          CodeInvalidFormat: string;
          DescriptionRequired: string;
          InvalidUrl: string;
          InvalidColor: string;
          MaxLength: string;
          DateRangeRequired: string;
          EndDateAfterStartDate: string;
        };
      };
      Messages: {
        CreateSuccess: string;
        CreateError: string;
        UpdateSuccess: string;
        UpdateError: string;
        PublishSuccess: string;
        ArchiveSuccess: string;
        UnpublishSuccess: string;
        CodeExistsError: string;
        DeleteConfirm: string;
        DeleteSuccess: string;
        DeleteError: string;
        RestoreConfirm: string;
        RestoreSuccess: string;
        RestoreError: string;
        LoadCampaignsError: string;
        EditCampaignTitle: string;
      };
    };
    SettlementPoolBoosts: {
      StardustBoost: string;
      ValorBoost: string;
      Regions: {
        CZ: string;
        SK: string;
      };
      BoostAmount: string;
      DistributionRatio: string;
      HunterPoolRatio: string;
      ValorPoolRatio: string;
      CurrentPoolTotal: string;
      PoolTotalLoadError: string;
      SaveButton: string;
      SaveSuccess: string;
      LoadingError: string;
      Validation: {
        BoostAmountNonNegative: string;
        RatioSumMustBe100: string;
      };
    };
    VatStatusRefresh: {
      Title: string;
      Description: string;
      Button: string;
      Loading: string;
      Success: string;
      Error: string;
    };
    PaymentSimulation: {
      Title: string;
      Description: string;
      PublicIDLabel: string;
      Button: string;
      Loading: string;
      ConfirmTitle: string;
      ConfirmText: string;
      Success: string;
      Errors: {
        NotFound: string;
        Forbidden: string;
        NotPayment: string;
        NotSandbox: string;
        StatusNotAllowed: string;
        Generic: string;
      };
    };
  };
  Devices: {
    List: {
      LoadingError: string;
      Name: string;
      PayPoint: string;
      DateCreated: string;
      LastUsedAt: string;
      RevokeConfirm: string;
      RevokeSuccess: string;
      RevokeError: string;
      RegenerateConfirm: string;
      RegenerateSuccess: string;
      RegenerateError: string;
      NewDeviceSecret: string;
      CopyDeviceSecret: string;
      DeviceSecretCopied: string;
      AddDevice: string;
      DeviceName: string;
      SelectPayPoint: string;
      LinkSuccess: string;
      LinkError: string;
      NoTerminalPayPoints: string;
      DeviceSecretWarning: string;
    };
  };
  PaymentGateways: {
    Legend: string;
    Description: string;
    ClientID: string;
    CopyClientID: string;
    ClientIDCopied: string;
    LastUsedAt: string;
    NoCredential: string;
    Generate: string;
    Regenerate: string;
    Revoke: string;
    Secret: {
      CreatedTitle: string;
      RegeneratedTitle: string;
      Warning: string;
      ClientID: string;
      ClientSecret: string;
      Copied: string;
      Close: string;
    };
    Messages: {
      GenerateError: string;
      RegenerateConfirmTitle: string;
      RegenerateConfirm: string;
      RegenerateError: string;
      RevokeConfirmTitle: string;
      RevokeConfirm: string;
      RevokeSuccess: string;
      RevokeError: string;
    };
  };
  TerminalPin: {
    Title: string;
    Description: string;
    NoPinYet: string;
    Reveal: string;
    Hide: string;
    Regenerate: string;
    RegenerateConfirm: string;
    RegenerateSuccess: string;
    RegenerateError: string;
  };
  UserSettings: {
    BalanceVisibility: {
      Title: string;
      Label: string;
      Description: string;
    };
    QuickPayment: {
      Title: string;
    };
  };
  Sso: {
    Consent: {
      Intro: string;
      PermissionsHeading: string;
      RevokeHint: string;
      Allow: string;
      Deny: string;
      Redirecting: string;
      ReauthTitle: string;
      ReauthText: string;
      ReauthButton: string;
      SignedInAs: string;
      UseAnotherAccount: string;
      MissingRequest: string;
    };
    ConnectedApps: {
      Empty: string;
      ConnectedAt: string;
      Disconnect: string;
      DisconnectConfirmTitle: string;
      DisconnectConfirm: string;
      DisconnectSuccess: string;
      DisconnectError: string;
      LoadError: string;
    };
    Scopes: {
      OpenId: string;
      Profile: string;
      Email: string;
      OfflineAccess: string;
      Contexts: string;
      Payments: string;
      Merchant: string;
      Hunter: string;
      TerminalsManage: string;
      DirectoryRead: string;
      CouponsCreate: string;
      LoyaltyRead: string;
    };
    Errors: {
      RequestNotFound: string;
      RequestExpired: string;
      RequestNotPending: string;
      IdentityRequired: string;
      IdentityNotActive: string;
      BackOfficeNotAllowed: string;
      ConsentNotFound: string;
      ClientNotFound: string;
      Unknown: string;
    };
  };
  Beta: {
    Badge: string;
    RoadmapUrl: string;
    AuthCard: {
      Title: string;
      Body: string;
    };
    Drawer: {
      Title: string;
      BodyFirst: string;
      BodySecond: string;
      BodyThird: string;
      Continue: string;
      Close: string;
    };
    MenuItem: string;
  };
}

export type TFunction = (
  resourcePath: ObjPathProxy<ResourceDictionary, string>,
  options?: any,
) => string;

export const Resources = createProxy<ResourceDictionary>();

export function getResourcePath(
  proxy: ObjPathProxy<ResourceDictionary, string>,
): string {
  const path = getPath(proxy);

  if (!path || path.length === 0) return "";
  if (path.length === 1) return path[0].toString();
  return `${path[0].toString()}:${path.slice(1).join(".")}`;
}

export type ExistsFunction = (
  resourcePath: ObjPathProxy<ResourceDictionary, string> | undefined,
) => boolean;

export function useResource(): { t: TFunction; exists: ExistsFunction } {
  const { t: i18Translation, i18n } = useTranslation();

  const t = useCallback(
    (resourcePath: ObjPathProxy<ResourceDictionary, string>, options?: any) =>
      i18Translation(
        getResourcePath(resourcePath),
        options,
      ) as unknown as string,
    [i18Translation],
  );

  const exists = useCallback(
    (resourcePath: ObjPathProxy<ResourceDictionary, string> | undefined) => {
      if (!resourcePath) {
        return false;
      }
      return i18n.exists(getResourcePath(resourcePath));
    },
    [i18n],
  );

  return { t, exists };
}

export function useServerError(
  parentObject: ObjPathProxy<ResourceDictionary, object>,
  fallbackProperty: ObjPathProxy<ResourceDictionary, string>,
) {
  const { t } = useResource();

  return {
    translateError: (code: string | null | undefined) => {
      // default in auth reducer is null, so if it's "" something wen't wrong
      if (code === "") {
        return t(fallbackProperty);
      }

      if (!code) {
        return null;
      }

      const newCode = `${getResourcePath(parentObject as any)}.${code}`;

      const resource: string = i18next.exists(newCode)
        ? i18next.t(newCode)
        : t(fallbackProperty);

      return resource;
    },
  };
}
